import React, { useState, useEffect, useRef } from "react";

// ── Shared foundation ──────────────────────────────────────────────
import { _T, C, CSS, setThemeGlobal, THEME_TAB_CLASS } from "./shared/theme";
import { playSound } from "./shared/soundEngine";
import { s_GB } from "./shared/styles";
import { useStorage, useDbField } from "./shared/hooks";
import { SEED_USERS, SEED_NOTIFS } from "./shared/constants";
import { initUser, getUserId, subscribeToPush, syncFriendRequest, syncFriendAccept, syncFriendDecline, notifyUser, trackSocialEvent, getGameSession, getActiveSessions, stopGameSession, sendHeartbeat, markGameInviteSeen, declineGameInvite, pollSync } from "./shared/dbService";
import { isNative, setupNativePush } from "./shared/nativePush";
import { App as CapApp } from "@capacitor/app";

// ── UI primitives ──────────────────────────────────────────────────
// (imported by feature components; re-exported here for ErrorBoundary wrapper)

// ── Modals & drawers ──────────────────────────────────────────────
import NotificationsDrawer from "./modals/NotificationsDrawer";
import TipsModal           from "./modals/TipsModal";
import RemindersSheet      from "./modals/RemindersSheet";
import LicenseSheet        from "./modals/LicenseSheet";
import LanguagePicker      from "./modals/LanguagePicker";
import GroupInvitePopup    from "./modals/GroupInvitePopup";
import TrialEndModal       from "./modals/TrialEndModal";
import { t, isRTL, LANGUAGES } from "./shared/i18n";

// ── Onboarding ────────────────────────────────────────────────────
import SplashScreen      from "./features/onboarding/SplashScreen";
import OnboardSlides     from "./features/onboarding/OnboardSlides";
import GoogleAuthScreen  from "./features/onboarding/GoogleAuthScreen";
import NameAgeScreen     from "./features/onboarding/NameAgeScreen";
import SportOnboard      from "./features/onboarding/SportOnboard";
import OnboardingCoach   from "./features/onboarding/OnboardingCoach";
import { usePendingReminders } from "./shared/usePendingReminders";

// ── Screens ───────────────────────────────────────────────────────
import Dashboard      from "./features/dashboard/Dashboard";
import GoalStatsScreen from "./features/dashboard/GoalStatsScreen";
import WeekScreen                from "./features/week/WeekScreen";
import AppointmentInviteScreen  from "./features/week/AppointmentInviteScreen";
import ExerciseScreen from "./features/exercises/ExerciseScreen";
import GamesScreen    from "./features/games/GamesScreen";
import FocusTapGame   from "./features/games/components/FocusTapGame";
import SnelleResetSession from "./features/exercises/components/SnelleResetSession";
import CoachScreen    from "./features/coach/CoachScreen";
import CoachDashboardScreen from "./features/coach/CoachDashboardScreen";
import GoalsScreen    from "./features/goals/GoalsScreen";
import SleepMoodScreen from "./features/sleep-mood/SleepMoodScreen";
import CommunityScreen from "./features/community/CommunityScreen";
import ProfilePage    from "./features/community/ProfilePage";
import BusinessScreen from "./features/business/BusinessScreen";
import BusinessPublicPage from "./features/business/BusinessPublicPage";
import AdminDashboardScreen from "./features/admin/AdminDashboardScreen";
import LabScreen from "./features/lab/LabScreen";
import WalletScreen from "./features/wallet/WalletScreen";
import FeatureTourModal, { TOUR_KEY } from "./modals/FeatureTourModal";
import BadgeUnlockModal from "./modals/BadgeUnlockModal";
import { THEME_UNLOCK_LEVEL } from "./shared/theme";

const BASE = process.env.REACT_APP_API_URL || '/api';

// ── Error boundary ────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{padding:32,color:"#ff6b6b",fontFamily:"monospace",background:"#0a0a0a",minHeight:"100vh"}}>
          <h2>💥 Runtime Error (check browser console for full trace)</h2>
          <pre style={{whiteSpace:"pre-wrap",fontSize:13}}>{this.state.error.toString()}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── ScreenShareModal ──────────────────────────────────────────────────────────
function ScreenShareModal({ onClose, lang }) {
  const nl = lang === 'nl';
  // scanning | found | connecting | connected | notfound
  const [phase, setPhase]       = React.useState('scanning');
  const [copied, setCopied]     = React.useState(false);
  const [connectErr, setConnectErr] = React.useState('');
  const [countdown, setCountdown]   = React.useState(5);
  const reqRef   = React.useRef(null);
  const availRef = React.useRef(null);

  const [sessionId] = React.useState(() => {
    let id = localStorage.getItem('sl_share_session');
    if (!id) { id = Math.random().toString(36).slice(2,10) + Date.now().toString(36); localStorage.setItem('sl_share_session', id); }
    return id;
  });

  const viewerUrl = `${window.location.origin}${window.location.pathname}?view=${sessionId}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=170x170&color=C8A84B&bgcolor=0f1e35&data=${encodeURIComponent(viewerUrl)}`;

  // Broadcast state to viewers
  React.useEffect(() => {
    const key = `sl_share_${sessionId}`;
    const iv = setInterval(() => {
      try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), url: window.location.href })); } catch {}
    }, 1000);
    return () => { clearInterval(iv); localStorage.removeItem(key); };
  }, [sessionId]);

  // Scan for presentation displays via PresentationRequest.getAvailability()
  // getAvailability() only reads availability — it never navigates or opens anything
  React.useEffect(() => {
    let cancelled = false;
    let fallbackTimer = null;

    if (!window.PresentationRequest) {
      // Browser has no support at all → show notfound after brief scanning UX
      fallbackTimer = setTimeout(() => { if (!cancelled) setPhase('notfound'); }, 2200);
      return () => { cancelled = true; clearTimeout(fallbackTimer); };
    }

    try {
      const req = new window.PresentationRequest([viewerUrl]);
      reqRef.current = req;

      req.getAvailability().then(avail => {
        if (cancelled) return;
        availRef.current = avail;

        const update = () => {
          if (!cancelled) setPhase(avail.value ? 'found' : 'notfound');
        };

        if (avail.value) {
          setPhase('found');
        } else {
          avail.onchange = update;
          // Wait up to 4 s for a device to appear before giving up
          fallbackTimer = setTimeout(() => {
            if (!cancelled && phase !== 'found') setPhase('notfound');
          }, 4000);
        }
      }).catch(() => {
        if (!cancelled) fallbackTimer = setTimeout(() => setPhase('notfound'), 600);
      });
    } catch {
      fallbackTimer = setTimeout(() => { if (!cancelled) setPhase('notfound'); }, 600);
    }

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      if (availRef.current) availRef.current.onchange = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-close countdown when not found
  React.useEffect(() => {
    if (phase !== 'notfound') return;
    setCountdown(5);
    const tick = setInterval(() => setCountdown(c => c - 1), 1000);
    const close = setTimeout(() => onClose(), 5000);
    return () => { clearInterval(tick); clearTimeout(close); };
  }, [phase, onClose]);

  // Connect — only called when phase==='found', so getAvailability already confirmed displays exist
  const connectToDisplay = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!reqRef.current) return;
    setConnectErr('');
    setPhase('connecting');
    try {
      const conn = await reqRef.current.start();
      setPhase('connected');
      conn.addEventListener('terminate', () => setPhase('found'));
    } catch (err) {
      // AbortError = user cancelled picker — no issue, just go back to found
      setPhase('found');
      if (err && err.name !== 'AbortError') {
        setConnectErr(nl ? 'Verbinding mislukt.' : 'Connection failed.');
      }
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(viewerUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9800,
      background:'rgba(5,12,24,0.88)', backdropFilter:'blur(8px)',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:'0 20px',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <style>{`
        @keyframes ss-bounce {
          0%  {opacity:0;transform:scale(.6) translateY(30px)}
          55% {opacity:1;transform:scale(1.04) translateY(-4px)}
          80% {transform:scale(.98) translateY(2px)}
          100%{opacity:1;transform:scale(1) translateY(0)}
        }
        @keyframes ss-pulse  { 0%,100%{opacity:.4} 50%{opacity:1} }
        @keyframes ss-radar  {
          0%   { transform:scale(1);   opacity:.7 }
          100% { transform:scale(2.8); opacity:0  }
        }
        @keyframes ss-spin   { to { transform:rotate(360deg) } }
        @keyframes ss-shake  {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-6px)}
          40%{transform:translateX(6px)}
          60%{transform:translateX(-4px)}
          80%{transform:translateX(4px)}
        }
      `}</style>

      <div style={{
        width:'100%', maxWidth:370,
        background:'linear-gradient(160deg,#0f1e35,#0a1522)',
        border:'1px solid rgba(200,168,75,0.3)',
        borderRadius:24, padding:'26px 22px 22px',
        boxShadow:'0 0 80px rgba(200,168,75,0.15)',
        animation:'ss-bounce .55s cubic-bezier(.34,1.56,.64,1) both',
        textAlign:'center', position:'relative', overflow:'hidden',
      }}>

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
          <div style={{fontSize:13,fontWeight:700,color:'rgba(200,168,75,.9)',letterSpacing:2,textTransform:'uppercase'}}>
            📺 {nl ? 'Scherm delen' : 'Share Screen'}
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,.35)',cursor:'pointer',fontSize:22,lineHeight:1,padding:0}}>&times;</button>
        </div>

        {/* ── PHASE: scanning ── */}
        {phase === 'scanning' && (
          <div style={{padding:'28px 0 20px'}}>
            <div style={{position:'relative',width:80,height:80,margin:'0 auto 20px'}}>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  position:'absolute', inset:0, borderRadius:'50%',
                  border:'2px solid rgba(200,168,75,.6)',
                  animation:`ss-radar 2s ${i*0.65}s ease-out infinite`,
                }}/>
              ))}
              <div style={{
                position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:30,
              }}>📡</div>
            </div>
            <div style={{fontSize:15,fontWeight:700,color:'#fff',marginBottom:6}}>
              {nl ? 'Zoeken naar TV…' : 'Scanning for TVs…'}
            </div>
            <div style={{fontSize:12,color:'rgba(255,255,255,.35)'}}>
              {nl ? 'Zorg dat je TV op hetzelfde netwerk zit' : 'Make sure your TV is on the same network'}
            </div>
          </div>
        )}

        {/* ── PHASE: found ── */}
        {phase === 'found' && (
          <div style={{padding:'10px 0 4px'}}>
            <div style={{fontSize:36,marginBottom:8}}>📺</div>
            <div style={{fontSize:15,fontWeight:700,color:'#22c55e',marginBottom:4}}>
              {nl ? 'Scherm gevonden!' : 'Display found!'}
            </div>
            <div style={{fontSize:12,color:'rgba(255,255,255,.4)',marginBottom:18}}>
              {nl ? 'Klik om te verbinden — je browser toont beschikbare apparaten' : 'Click to connect — your browser will list available devices'}
            </div>
            <button onClick={connectToDisplay} style={{
              width:'100%', padding:'14px 0',
              background:'linear-gradient(135deg,#166534,#15803d)',
              border:'1px solid rgba(34,197,94,0.5)',
              borderRadius:14, cursor:'pointer',
              fontSize:15, fontWeight:700, color:'#fff',
              display:'flex', alignItems:'center', justifyContent:'center', gap:10,
            }}>
              <span>📡</span> {nl ? 'Verbinden met scherm' : 'Connect to display'}
            </button>
            {connectErr && <div style={{marginTop:8,fontSize:12,color:'#f87171'}}>{connectErr}</div>}
          </div>
        )}

        {/* ── PHASE: connecting ── */}
        {phase === 'connecting' && (
          <div style={{padding:'28px 0 20px'}}>
            <div style={{
              width:44,height:44,borderRadius:'50%',
              border:'3px solid rgba(200,168,75,.2)',
              borderTopColor:'#C8A84B',
              animation:'ss-spin .8s linear infinite',
              margin:'0 auto 16px',
            }}/>
            <div style={{fontSize:14,color:'rgba(255,255,255,.7)',fontWeight:600}}>
              {nl ? 'Browser opent apparatenlijst…' : 'Opening device picker…'}
            </div>
          </div>
        )}

        {/* ── PHASE: connected ── */}
        {phase === 'connected' && (
          <div style={{padding:'16px 0 8px'}}>
            <div style={{fontSize:40,marginBottom:10}}>✅</div>
            <div style={{fontSize:15,fontWeight:700,color:'#22c55e',marginBottom:6}}>
              {nl ? 'Verbonden!' : 'Connected!'}
            </div>
            <div style={{fontSize:12,color:'rgba(255,255,255,.4)'}}>
              {nl ? 'Je scherm wordt gedeeld op de TV.' : 'Your screen is being shared on the TV.'}
            </div>
          </div>
        )}

        {/* ── PHASE: notfound — prominent alert panel, then auto-closes ── */}
        {phase === 'notfound' && (
          <div style={{
            background:'rgba(239,68,68,0.08)',
            border:'1px solid rgba(239,68,68,0.3)',
            borderRadius:16, padding:'20px 16px', marginBottom:16,
            animation:'ss-shake .4s ease',
          }}>
            <div style={{fontSize:32,marginBottom:8}}>📺</div>
            <div style={{fontSize:15,fontWeight:700,color:'#f87171',marginBottom:6}}>
              {nl ? 'Geen TV gevonden' : 'No TV found'}
            </div>
            <div style={{fontSize:12,color:'rgba(255,255,255,.45)',lineHeight:1.6,marginBottom:12}}>
              {nl
                ? 'Je browser ondersteunt geen schermcasting, of er zijn geen apparaten gevonden op dit netwerk.'
                : "Your browser doesn't support screen casting, or no devices were found on this network."}
            </div>
            <div style={{fontSize:12,color:'rgba(255,255,255,.3)'}}>
              {nl ? `Sluit automatisch over ${countdown}s` : `Auto-closing in ${countdown}s`}
            </div>
          </div>
        )}

        {/* ── QR + copy (always visible except connecting) ── */}
        {phase !== 'connecting' && (
          <>
            <div style={{
              width:1, height:1, background:'rgba(255,255,255,.08)',
              margin:'16px auto',
            }}/>
            <div style={{fontSize:11,color:'rgba(255,255,255,.3)',marginBottom:10,letterSpacing:.5}}>
              {nl ? 'Of scan met telefoon / TV-browser:' : 'Or scan with phone / TV browser:'}
            </div>
            <div style={{
              background:'#0f1e35', borderRadius:14,
              padding:10, marginBottom:14,
              border:'1px solid rgba(200,168,75,0.2)',
              display:'inline-block',
            }}>
              <img src={qrUrl} alt="QR" width={170} height={170} style={{display:'block',borderRadius:6}}/>
            </div>

            {/* Live dot */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,marginBottom:12}}>
              <span style={{width:7,height:7,borderRadius:'50%',background:'#22c55e',display:'inline-block',animation:'ss-pulse 1.2s ease-in-out infinite'}}/>
              <span style={{fontSize:11,color:'rgba(255,255,255,.35)',fontWeight:600}}>LIVE</span>
            </div>

            {/* URL copy */}
            <div style={{display:'flex',gap:8}}>
              <div style={{
                flex:1, background:'rgba(255,255,255,.04)',
                border:'1px solid rgba(255,255,255,.1)',
                borderRadius:10, padding:'8px 10px',
                fontSize:11, color:'rgba(255,255,255,.35)',
                textAlign:'left', overflow:'hidden',
                textOverflow:'ellipsis', whiteSpace:'nowrap',
                fontFamily:'monospace',
              }}>{viewerUrl}</div>
              <button onClick={copyLink} style={{
                padding:'8px 14px', borderRadius:10, border:'1px solid rgba(200,168,75,.3)',
                background: copied ? 'rgba(34,197,94,.15)' : 'rgba(200,168,75,.1)',
                color: copied ? '#22c55e' : '#C8A84B',
                cursor:'pointer', fontSize:13, fontWeight:700, whiteSpace:'nowrap',
                transition:'all .2s',
              }}>
                {copied ? '✓' : nl ? 'Kopieer' : 'Copy'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── VideoIntro — plays sl-onboarding-vid.mp4, fades out, calls onDone ────────
function VideoIntro({ onDone }) {
  const [fading, setFading] = React.useState(false);

  React.useEffect(() => {
    // Start fade at 7.2s so the fade-out (0.8s) finishes right at ~8s
    const fadeTimer = setTimeout(() => setFading(true), 7200);
    const doneTimer = setTimeout(() => onDone(), 8000);
    return () => { clearTimeout(fadeTimer); clearTimeout(doneTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9600,
      background: '#000',
      opacity: fading ? 0 : 1,
      transition: 'opacity 0.8s ease-in-out',
      pointerEvents: fading ? 'none' : 'auto',
    }}>
      <video
        src="/sl-onboarding-vid.mp4"
        autoPlay
        playsInline
        muted
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────
export default function App() {
  const [lang,setLang]                         = useStorage("sl_lang","nl");
  const [onboardDone,setOnboardDone]           = useStorage("sl_onboard_done",false);
  const [weekIntention,setWeekIntention]       = useDbField("sl_intention","");
  const [weekAgenda,setWeekAgenda]             = useDbField("sl_agenda",{});
  const [datedAgenda,setDatedAgenda]           = useDbField("sl_agenda_dated",{});
  const [userName,setUserName]                 = useDbField("sl_name","");
  const currentUserId = localStorage.getItem('sl_user_id') || (userName ? userName.toLowerCase().replace(/\s+/g,"_") : "me");
  const [themeId,setThemeIdRaw]               = useStorage("sl_theme","gold");
  const [myPosts,setMyPosts]                   = useDbField("sl_my_posts",[]);
  const [friends,setFriends]                   = useState([]);
  const [friendRequests,setFriendRequests]     = useState([]);
  const [conversations,setConversations]       = useState({});
  const [connStatus,setConnStatus]             = useState('loading'); // 'loading'|'online'|'offline'
  const connFailRef                            = useRef(0);
  const [myBannerColor,setMyBannerColor]       = useDbField("sl_banner_color","#0d1625");
  const [myAccentColor,setMyAccentColor]       = useDbField("sl_accent_color","#C8A84B");
  const [license,setLicense]                   = useDbField("sl_license","freemium");
  const [isAdmin,setIsAdmin]                   = useState(false);
  const [isResearcher,setIsResearcher]         = useState(false);
  const [walletBalance,setWalletBalance]       = useState(0);
  const [bizWalletBalance,setBizWalletBalance] = useState(0);
  const [showAdmin,setShowAdmin]               = useState(false);
  const [businessId,setBusinessId]             = useState(null);
  const [bizDisplayName,setBizDisplayName]     = useState('');
  const [bizDisplayLogo,setBizDisplayLogo]     = useState('');
  const [businesses,setBusinesses]             = useState([]);
  const [notifications,setNotifications]       = useDbField("sl_notifs",SEED_NOTIFS);
  const [tab,setTab]                           = useState(() => { try { const s=localStorage.getItem('sl_last_tab'); return s!==null?parseInt(s,10):0; } catch{return 0;} });
  const [communityInitTab,setCommunityInitTab] = useState(null);
  const [gamePresetPartner,setGamePresetPartner] = useState(null);
  const [onboardStep,setOnboardStep]           = useState("splash");
  // ── Sport onboarding flow (in-app guided experience after signup) ──────────
  const [sportFlowPhase, setSportFlowPhase]   = useState(null); // null|'tab_light'|'game_prompt'|'game_running'|'exercise_prompt'|'exercise_running'|'dash_tutorial'
  const [litNavTabs,     setLitNavTabs]       = useState(new Set());
  const [dashTutStep,    setDashTutStep]      = useState(0);    // tutorial step index
  const [tutTyped,       setTutTyped]         = useState('');   // typing effect text
  const [profileTarget,setProfileTarget]       = useState(null);
  const [bizTransition,setBizTransition]       = useState(null); // {phase:'out'|'flash'|'in', to: 10|0}
  const [viewBizPageId,setViewBizPageId]       = useState(null); // in-app public business page overlay
  const [showNotifs,setShowNotifs]             = useState(false);
  const [showTips,setShowTips]                 = useState(false);
  const [showSettingsMenu,setShowSettingsMenu] = useState(false);
  const [showScreenShare,setShowScreenShare]   = useState(false);
  const [showProfileDropdown,setShowProfileDropdown] = useState(false);
  const profileClickTimer = useRef(null);
  const [showReminders,setShowReminders]       = useState(false);
  const [showLicense,setShowLicense]           = useState(false);
  const [showTrialEnd,setShowTrialEnd]         = useState(false);
  const [trialEndVariant,setTrialEndVariant]   = useState('last_day');
  const [showLangPicker,setShowLangPicker]     = useState(false);
  const [showIosInstall,setShowIosInstall]     = useState(false);
  const [userAge,setUserAge]                   = useDbField("sl_age",null);
  const [signedUpAt,setSignedUpAt]             = useDbField("sl_signed_up",null);
  const [sleepLog]                             = useDbField("sl_sleep_log",[]);
  const [googleName,setGoogleName]             = useState("");
  const [googlePicture,setGooglePicture]       = useState("");
  const [googleId,setGoogleId]                 = useState("");
  const [installPrompt,setInstallPrompt]       = useState(null);   // Android deferred prompt
  const [showIosBanner,setShowIosBanner]       = useState(false);
  const [showTour,setShowTour]                 = useState(false);
  const [paymentToast,setPaymentToast]         = useState(null);
  const [pendingInvite,setPendingInvite]       = useState(null);
  const [showInviteModal,setShowInviteModal]   = useState(false);
  const [inviteDetails,setInviteDetails]       = useState(null);
  const [inviteResponded,setInviteResponded]   = useState(false);
  const [pendingApptInvite,setPendingApptInvite] = useState(null);
  const [apptInviteDetails,setApptInviteDetails] = useState(null);
  const [showApptInviteScreen,setShowApptInviteScreen] = useState(false);
  const deferredPrompt                         = useRef(null);
  const [guestExpired,setGuestExpired]         = useState(false);
  const [friendInviteModal,setFriendInviteModal] = useState(null); // {from_name, token, status}
  const [pendingGameInvite,setPendingGameInvite] = useState(null); // {token, gameTitle, inviterName, mode}
  const [gameInvitePopup,setGameInvitePopup]   = useState(null); // {token, gameTitle, inviterName, mode}
  const [activeSessions,setActiveSessions]     = useState([]);   // active game sessions for dashboard
  const [bizInvitePopup,setBizInvitePopup]     = useState(null); // {token, biz_name, logo_url, inviter_name, role}
  const [newMsgToast,setNewMsgToast]           = useState(null); // {name, uid, convKey}
  const [friendOnlineToast,setFriendOnlineToast] = useState(null); // {name}
  const [badgeUnlock,setBadgeUnlock]           = useState(null); // {type, level}
  const [gameLevelUp,setGameLevelUp]           = useState(null); // {gameId, oldLevel, newLevel}
  const [themeUnlock,setThemeUnlock]           = useState(null); // {themeId, level}
  const [coachHireToast,setCoachHireToast]     = useState(null); // {name}
  const [contractStatusToast,setContractStatusToast] = useState(null); // {status:'accepted'|'declined'}
  const [creditsToast,setCreditsToast]         = useState(null); // {text}
  const [competitionToast,setCompetitionToast] = useState(null); // {text}
  const [competitionPopup,setCompetitionPopup] = useState(null); // {text, name}
  const [groupChatToast,setGroupChatToast]     = useState(null); // {groupName, senderName}
  const [pulsePopup,setPulsePopup]             = useState(null); // {pulse, businessId, answered}
  const [bookingToast,setBookingToast]         = useState(null); // {name}
  const [friendReqToast,setFriendReqToast]     = useState(null); // {name}
  const [adminRightsToast,setAdminRightsToast] = useState(false);
  const [labRightsToast,setLabRightsToast]     = useState(false);
  const [onboardGamesOpen,setOnboardGamesOpen] = useState(false);
  const prevOnlineFriendsRef = useRef(new Set());
  const friendProfilesRef    = useRef({});     // uid → name
  const prevNotifIdsRef      = useRef(new Set());
  const seenInviteTokens                       = useRef(new Set());
  const badgeInitRef         = useRef(false);
  const prevBadgesRef        = useRef({});
  const nl = lang === 'nl';

  // ── Persist last visited tab ────────────────────────────────────
  useEffect(() => {
    try { localStorage.setItem('sl_last_tab', String(tab)); } catch {}
  }, [tab]);

  useEffect(() => {
    // Android: capture the browser's install prompt
    // Skip if already running as installed PWA or inside native Capacitor app
    const onBeforeInstall = e => {
      if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || isNative()) return;
      e.preventDefault(); deferredPrompt.current = e; setInstallPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    // iOS: show banner if on an iOS device and not already installed
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const dismissed = localStorage.getItem('sl_ios_banner_dismissed');
    if (isIos && !isStandalone && !dismissed) setShowIosBanner(true);
    // Check for Stripe redirect result
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      const plan = params.get('plan');
      setPaymentToast({ success: true, plan });
      window.history.replaceState({}, '', '/');
      if (plan === 'companies') localStorage.setItem('sl_redirect_to_biz', '1');
      setTimeout(() => setPaymentToast(null), 6000);
    } else if (params.get('payment') === 'biz_credits') {
      const credits = parseInt(params.get('credits') || '0', 10);
      setPaymentToast({ success: true, credits, isBiz: true });
      window.history.replaceState({}, '', '/');
      setTimeout(() => setPaymentToast(null), 6000);
    } else if (params.get('payment') === 'credits') {
      const credits = parseInt(params.get('credits') || '0', 10);
      setPaymentToast({ success: true, credits });
      window.history.replaceState({}, '', '/');
      // Reload wallet balance from server (DB-first: no localStorage write)
      const uid = localStorage.getItem('sl_user_id');
      if (uid) {
        fetch(`${BASE}/wallet/${encodeURIComponent(uid)}`).then(r=>r.json()).then(d=>{
          if (d.balance !== undefined) setWalletBalance(d.balance);
        }).catch(()=>{});
      }
      setTimeout(() => setPaymentToast(null), 6000);
    } else if (params.get('payment') === 'cancelled') {
      setPaymentToast({ success: false });
      window.history.replaceState({}, '', '/');
      setTimeout(() => setPaymentToast(null), 4000);
    }
    // Check for invite token in URL (?invite=TOKEN&action=accept|decline)
    const inviteToken  = params.get('invite');
    const inviteAction = params.get('action');
    if (inviteToken) {
      localStorage.setItem('sl_pending_invite', JSON.stringify({ token: inviteToken, action: inviteAction || null }));
      window.history.replaceState({}, '', '/');
    }
    // Check for friend invite token in URL (?friendinvite=TOKEN)
    const friendInvite = params.get('friendinvite');
    if (friendInvite) {
      localStorage.setItem('sl_pending_friend_invite', friendInvite);
      window.history.replaceState({}, '', '/');
    }
    // Check for appointment invite token in URL (?apptinvite=TOKEN)
    const apptInvite = params.get('apptinvite');
    if (apptInvite) {
      localStorage.setItem('sl_pending_appt_invite', apptInvite);
      window.history.replaceState({}, '', '/');
    }
    // Check for game invite token in URL (?gameinvite=TOKEN)
    const gameInvite = params.get('gameinvite');
    if (gameInvite) {
      localStorage.setItem('sl_pending_game_invite', gameInvite);
      window.history.replaceState({}, '', '/');
    }
    // Check for business invite token in URL (?bizinvite=TOKEN)
    const bizInvite = params.get('bizinvite');
    if (bizInvite) {
      localStorage.setItem('sl_pending_biz_invite', bizInvite);
      window.history.replaceState({}, '', '/');
    }
    // Check for contacts invite token (?invite=TOKEN&ref=UID — from contacts feature)
    const contactInvite = params.get('invite');
    const contactRef    = params.get('ref');
    if (contactInvite && contactRef) {
      // Store separately so it doesn't collide with existing invite handling
      localStorage.setItem('sl_contact_invite', JSON.stringify({ token: contactInvite, ref: contactRef }));
      window.history.replaceState({}, '', '/');
    }
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  // ── DB hydration on mount ──────────────────────────────────────────
  // Ensures a user row exists and pulls latest data from MariaDB into
  // localStorage so all useStorage hooks reflect the DB state.
  useEffect(() => {
    const googleId      = localStorage.getItem("sl_google_id")      || undefined;
    const googleEmail   = localStorage.getItem("sl_google_email")   || undefined;
    const googlePicture = localStorage.getItem("sl_google_picture") || undefined;
    const name          = localStorage.getItem("sl_name")
                          ? JSON.parse(localStorage.getItem("sl_name")) : undefined;
    initUser({ googleId, googleEmail, googlePicture, name }).then((result) => {
      if (!result)        { setConnStatus('offline'); return; }
      if (result.expired) { setGuestExpired(true);   return; }
      const d = result.data || {};
      // ── Set all React state directly from DB response ──────────────────────
      if (d.sl_onboard_done)     setOnboardDone(true);
      if (d.sl_age)              setUserAge(d.sl_age);
      if (d.sl_google_picture)   setGooglePicture(d.sl_google_picture);
      if (d.sl_name)             setUserName(d.sl_name);
      if (Array.isArray(d.sl_my_posts) && d.sl_my_posts.length > 0) {
        setMyPosts(d.sl_my_posts.slice(0, 30).map(p => ({ ...p, img: null })));
      }
      if (Array.isArray(d.sl_notifs)) {
        setNotifications(d.sl_notifs.map(n => ({ ...n, _synced: true })));
      }
      if (d.sl_signed_up)        setSignedUpAt(d.sl_signed_up);
      if (d.sl_license) {
        let lic = d.sl_license;
        if (d.sl_license_expires_at) {
          try { if (new Date(d.sl_license_expires_at) < new Date()) lic = 'freemium'; } catch {}
        }
        setLicense(lic);
      }
      if (d.sl_is_admin !== undefined)      setIsAdmin(!!d.sl_is_admin);
      if (d.sl_is_researcher !== undefined) setIsResearcher(!!d.sl_is_researcher);
      if (d.sl_wallet_balance !== undefined) setWalletBalance(d.sl_wallet_balance || 0);
      if (d.sl_intention)        setWeekIntention(d.sl_intention);
      if (d.sl_agenda && typeof d.sl_agenda === 'object') setWeekAgenda(d.sl_agenda);
      // Business state — DB is the only source of truth, no localStorage
      if (Array.isArray(d.sl_businesses)) setBusinesses(d.sl_businesses);
      // Determine active business: use stored ID if valid, otherwise default to first in list
      const activeBizId = d.sl_business_id ||
        (Array.isArray(d.sl_businesses) && d.sl_businesses.length > 0 ? d.sl_businesses[0].id : null);
      const activeBizRow = Array.isArray(d.sl_businesses)
        ? d.sl_businesses.find(b => b.id === activeBizId) || d.sl_businesses[0]
        : null;
      if (activeBizId) {
        setBusinessId(activeBizId);
        setBizDisplayName(activeBizRow?.name || d.sl_business_name || '');
        setBizDisplayLogo(activeBizRow?.logo_url || d.sl_business_logo || '');
        // If no stored active business in DB, persist the default to DB now
        if (!d.sl_business_id) {
          const uid = localStorage.getItem('sl_user_id');
          if (uid) fetch(`${BASE}/businesses/activate`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, business_id: activeBizId }),
          }).catch(() => {});
        }
      }
      if (d.sl_theme)            { setThemeIdRaw(d.sl_theme); setThemeGlobal(d.sl_theme); }
      if (d.sl_banner_color)     setMyBannerColor(d.sl_banner_color);
      if (d.sl_accent_color)     setMyAccentColor(d.sl_accent_color);
      // Google identity: set by auth flows and stored as raw strings in localStorage
      const gid    = localStorage.getItem('sl_google_id');
      const method = localStorage.getItem('sl_auth_method');
      if (gid)                   setGoogleId(gid);
      else if (method === 'email') setGoogleId('email_auth');
      // Post-payment redirect to business setup
      if (localStorage.getItem('sl_redirect_to_biz') === '1') {
        localStorage.removeItem('sl_redirect_to_biz');
        setTab(10);
      }
      // If initUser didn't return a businesses list, fetch it separately.
      // If it did, we already called setBusinesses above — no need to re-fetch
      // (that would overwrite good data with a second call that may differ).
      // The wallet is fetched automatically by the businessId useEffect.
      if (!Array.isArray(d.sl_businesses) || d.sl_businesses.length === 0) {
        const uidForBiz = localStorage.getItem('sl_user_id');
        if (uidForBiz) loadMyBusinesses(uidForBiz, d.sl_business_id || null);
      }
      // Subscribe to push — native app uses FCM/APNs, web uses ServiceWorker
      if (isNative()) {
        const uid = localStorage.getItem('sl_user_id');
        setupNativePush(uid);
      } else {
        subscribeToPush();
      }
    });
    void getUserId;
  }, []);

  // Re-subscribe to push every time the user brings the app to the foreground.
  // This keeps the subscription fresh and ensures it is always registered under
  // the current user ID (covers login, ID migration, subscription expiry).
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') subscribeToPush();
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, []);

  // ── Game level-up event (fired by postXp in utils.js) ───────────────────
  useEffect(() => {
    const handler = e => {
      const { gameId, oldLevel, newLevel } = e.detail;
      setGameLevelUp({ gameId, oldLevel, newLevel });
      playSound('gift');
      // Check if any theme just unlocked (compare highest game level reached)
      try {
        const levels = JSON.parse(localStorage.getItem('sl_game_levels') || '{}');
        const maxLevel = Math.max(...Object.values(levels).map(v => v.level || 1), newLevel);
        const seenKey = 'sl_theme_unlocks_seen';
        const seen = JSON.parse(localStorage.getItem(seenKey) || '[]');
        Object.entries(THEME_UNLOCK_LEVEL).forEach(([tid, reqLevel]) => {
          if (reqLevel && maxLevel >= reqLevel && !seen.includes(tid)) {
            seen.push(tid);
            localStorage.setItem(seenKey, JSON.stringify(seen));
            setTimeout(() => setThemeUnlock({ themeId: tid, level: reqLevel }), 1800); // show after game level popup
          }
        });
      } catch {}
    };
    window.addEventListener('sl:gamelevelup', handler);
    return () => window.removeEventListener('sl:gamelevelup', handler);
  }, []);

  // ── Unified real-time social sync every 8 s ───────────────────────────────
  // Updates notifications, friends, friend requests, and conversations in one call.
  useEffect(() => {
    if (!onboardDone) return;
    const uid = localStorage.getItem('sl_user_id');
    if (!uid) return;

    const sync = async () => {
      const data = await pollSync(uid);
      if (!data) {
        connFailRef.current += 1;
        if (connFailRef.current >= 2) setConnStatus('offline');
        return;
      }
      connFailRef.current = 0;
      setConnStatus('online');

      // ── Notifications ──────────────────────────────────────────────────────
      if (Array.isArray(data.notifications)) {
        // Check for new unread chat notifications → show in-app toast
        // Also check for biz_invite notifications → show invite popup
        for (const n of data.notifications) {
          if (n.type === 'chat_message' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            const currentTab = tab;
            if (currentTab !== 7) {  // not currently on chat tab
              const senderName = n.from || (lang === 'nl' ? 'Vriend' : 'Friend');
              setNewMsgToast({ name: senderName, uid: null });
              playSound('message');
              try { if (navigator.vibrate) navigator.vibrate(80); } catch {}
            }
          }
          // coach_hire → toast for coach
          if (n.type === 'coach_hire' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            setCoachHireToast({ name: n.from_name || n.from || (lang === 'nl' ? 'Iemand' : 'Someone') });
          }
          // coach_contract_accepted / declined → toast for client
          if ((n.type === 'coach_contract_accepted' || n.type === 'coach_contract_declined') && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            setContractStatusToast({ status: n.type === 'coach_contract_accepted' ? 'accepted' : 'declined', coachName: n.from_name || n.from || 'Coach' });
          }
          // booking_new → toast for business owner
          if (n.type === 'booking_new' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            setBookingToast({ name: n.from_name || n.from || (lang === 'nl' ? 'Iemand' : 'Someone') });
          }
          // credits_received → toast
          if (n.type === 'credits_received' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            setCreditsToast({ text: n.text || (lang === 'nl' ? 'Credits ontvangen!' : 'Credits received!') });
            playSound('gift');
          }
          // competition_new → popup
          if (n.type === 'competition_new' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            setCompetitionToast({ text: n.text || (lang === 'nl' ? 'Nieuwe competitie!' : 'New competition!') });
            setCompetitionPopup({ text: n.text || (lang === 'nl' ? 'Nieuwe competitie gestart!' : 'New competition started!'), name: n.from_name || n.from || '' });
            playSound('gift');
          }
          // group_chat → toast (unread group message)
          if (n.type === 'group_chat' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            const currentTab = tab;
            if (currentTab !== 7) {
              setGroupChatToast({ groupName: n.group_name || (lang === 'nl' ? 'Groep' : 'Group'), senderName: n.from_name || n.from || '' });
              playSound('message');
              try { if (navigator.vibrate) navigator.vibrate(80); } catch {}
            }
          }
          // pulse_check → popup for team members
          if (n.type === 'pulse_check' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            try {
              const meta = typeof n.meta === 'string' ? JSON.parse(n.meta) : (n.meta || {});
              const bizId = meta.business_id || businessId;
              if (bizId) {
                fetch(`${BASE}/businesses/${encodeURIComponent(bizId)}/pulse`)
                  .then(r => r.ok ? r.json() : [])
                  .then(pulses => {
                    const seenPulses = JSON.parse(localStorage.getItem('sl_pulse_seen') || '[]');
                    const unanswered = (Array.isArray(pulses) ? pulses : []).find(p => p.active && !seenPulses.includes(p.id));
                    if (unanswered) setPulsePopup({ pulse: unanswered, businessId: bizId, answered: false });
                  }).catch(() => {});
              }
            } catch {}
          }
          // friend_request → small toast (drawer already handles accept/decline)
          if (n.type === 'friend_request' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            setFriendReqToast({ name: n.from_name || n.from || (lang === 'nl' ? 'Iemand' : 'Someone') });
          }
          // admin_rights / lab_rights granted by admin
          if (n.type === 'admin_rights' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            setAdminRightsToast(true);
          }
          if (n.type === 'lab_rights' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            setLabRightsToast(true);
          }
          // biz_invite notification → fetch invite info and show popup
          if (n.type === 'biz_invite' && !n.read && n.id && !prevNotifIdsRef.current.has(n.id)) {
            try {
              const meta = typeof n.meta === 'string' ? JSON.parse(n.meta) : (n.meta || {});
              const token = meta.token;
              if (token && !localStorage.getItem('sl_pending_biz_invite') && !bizInvitePopup) {
                fetch(`${BASE}/business-invite/${token}`)
                  .then(r => r.ok ? r.json() : null)
                  .then(d => { if (d && !d.error && d.status === 'pending') setBizInvitePopup({ ...d, token }); })
                  .catch(() => {});
              }
            } catch {}
          }
          if (n.id) prevNotifIdsRef.current.add(n.id);
        }

        setNotifications(prev => {
          // Deduplicate DB notifications by fingerprint (keep highest id)
          const seenFps = new Set();
          const deduped = [...data.notifications]
            .sort((a, b) => (b.id || 0) - (a.id || 0))
            .filter(n => {
              const fp = `${n.type}|${n.text}`;
              if (seenFps.has(fp)) return false;
              seenFps.add(fp);
              return true;
            });
          const dbIds = new Set(deduped.map(n => n.id));
          const localOnly = prev.filter(n => !dbIds.has(n.id) && !seenFps.has(`${n.type}|${n.text}`));
          const fresh = deduped.map(n => ({ ...n, _synced: true }));
          return [...fresh, ...localOnly].sort((a, b) => (b.id || 0) - (a.id || 0));
        });
      }

      // ── Friends — full replace from DB ─────────────────────────────────────
      if (Array.isArray(data.friends)) {
        setFriends(data.friends);
      }

      // ── Friend requests — full replace from DB ─────────────────────────────
      if (Array.isArray(data.friend_requests)) {
        setFriendRequests(data.friend_requests);
      }

      // ── Conversations — full replace from DB (source of truth) ────────────
      if (data.conversations && typeof data.conversations === 'object') {
        setConversations(prev => {
          // Preserve pending (unsent) temp messages — id < real DB ids
          const merged = { ...data.conversations };
          for (const [convId, dbMsgs] of Object.entries(merged)) {
            const local = prev[convId] || [];
            const dbIds = new Set((dbMsgs || []).map(m => m.id));
            // Never downgrade read:true → unread (handles timing race with markChatRead)
            const localReadIds = new Set(local.filter(m => m.read).map(m => m.id));
            const withRead = (dbMsgs || []).map(m => localReadIds.has(m.id) ? { ...m, read: true } : m);
            const pending = local.filter(m => !dbIds.has(m.id) && typeof m.id === 'number' && m.id > 1e12 && m.id < 1e15);
            merged[convId] = pending.length > 0
              ? [...withRead, ...pending].sort((a,b) => (a.id||0)-(b.id||0))
              : withRead;
          }
          return merged;
        });
      }

      // ── Friend profiles cache ─────────────────────────────────────────────
      if (data.friend_profiles && typeof data.friend_profiles === 'object') {
        Object.assign(friendProfilesRef.current, data.friend_profiles);
      }

      // ── Friend online notifications ───────────────────────────────────────
      if (Array.isArray(data.online_friends)) {
        const newOnlineSet = new Set(data.online_friends);
        for (const fid of data.online_friends) {
          if (!prevOnlineFriendsRef.current.has(fid)) {
            const name = friendProfilesRef.current[fid] || (lang === 'nl' ? 'Vriend' : 'Friend');
            setFriendOnlineToast({ name });
            break; // show one at a time
          }
        }
        prevOnlineFriendsRef.current = newOnlineSet;
      }
    };

    sync(); // immediate on mount
    const id = setInterval(sync, 8000);
    return () => clearInterval(id);
  }, [onboardDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-dismiss toasts ────────────────────────────────────────────────────
  useEffect(() => {
    if (!newMsgToast) return;
    const t = setTimeout(() => setNewMsgToast(null), 4000);
    return () => clearTimeout(t);
  }, [newMsgToast]);

  useEffect(() => {
    if (!friendOnlineToast) return;
    const t = setTimeout(() => setFriendOnlineToast(null), 3000);
    return () => clearTimeout(t);
  }, [friendOnlineToast]);

  useEffect(() => { if (!coachHireToast) return; const t = setTimeout(() => setCoachHireToast(null), 5000); return () => clearTimeout(t); }, [coachHireToast]);
  useEffect(() => { if (!contractStatusToast) return; const t = setTimeout(() => setContractStatusToast(null), 5000); return () => clearTimeout(t); }, [contractStatusToast]);
  useEffect(() => { if (!creditsToast) return; const t = setTimeout(() => setCreditsToast(null), 4000); return () => clearTimeout(t); }, [creditsToast]);
  useEffect(() => { if (!competitionToast) return; const t = setTimeout(() => setCompetitionToast(null), 4000); return () => clearTimeout(t); }, [competitionToast]);
  useEffect(() => { if (!groupChatToast) return; const t = setTimeout(() => setGroupChatToast(null), 5000); return () => clearTimeout(t); }, [groupChatToast]);

  // Check for unanswered active pulse on login (team members)
  useEffect(() => {
    if (!onboardDone) return;
    const bizId = businessId;
    if (!bizId) return;
    const seenKey = 'sl_pulse_seen';
    fetch(`${BASE}/businesses/${encodeURIComponent(bizId)}/pulse`)
      .then(r => r.ok ? r.json() : [])
      .then(pulses => {
        const seen = JSON.parse(localStorage.getItem(seenKey) || '[]');
        const unanswered = (Array.isArray(pulses) ? pulses : []).find(p => p.active && !seen.includes(p.id));
        if (unanswered) setTimeout(() => setPulsePopup({ pulse: unanswered, businessId: bizId, answered: false }), 2000);
      }).catch(() => {});
  }, [onboardDone, businessId]); // eslint-disable-line
  useEffect(() => { if (!bookingToast) return; const t = setTimeout(() => setBookingToast(null), 5000); return () => clearTimeout(t); }, [bookingToast]);
  useEffect(() => { if (!friendReqToast) return; const t = setTimeout(() => setFriendReqToast(null), 4000); return () => clearTimeout(t); }, [friendReqToast]);
  useEffect(() => { if (!adminRightsToast) return; const t = setTimeout(() => setAdminRightsToast(false), 5000); return () => clearTimeout(t); }, [adminRightsToast]);
  useEffect(() => { if (!labRightsToast) return; const t = setTimeout(() => setLabRightsToast(false), 5000); return () => clearTimeout(t); }, [labRightsToast]);

  // ── Heartbeat every 60 s to keep presence status accurate ─────────────────
  useEffect(() => {
    if (!onboardDone) return;
    sendHeartbeat(); // immediate on load
    const id = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(id);
  }, [onboardDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Badge unlock detection (poll every 60 s) ──────────────────────────────
  useEffect(() => {
    if (!onboardDone || !currentUserId) return;
    const LEVELS = [null, 'bronze', 'silver', 'gold'];
    const checkBadges = async () => {
      try {
        const res = await fetch(`${BASE}/profile/${encodeURIComponent(currentUserId)}/badges`);
        if (!res.ok) return;
        const data = await res.json();
        const curr = {
          game:      data.game?.badge      || null,
          exercise:  data.exercise?.badge  || null,
          streak:    data.streak?.badge    || null,
          stability: data.stability?.badge || null,
          social:    data.social?.badge    || null,
        };
        if (!badgeInitRef.current) {
          // First fetch — just store baseline, don't fire popup
          prevBadgesRef.current = curr;
          badgeInitRef.current = true;
          return;
        }
        for (const [type, level] of Object.entries(curr)) {
          if (level && LEVELS.indexOf(level) > LEVELS.indexOf(prevBadgesRef.current[type] || null)) {
            setBadgeUnlock({ type, level });
            playSound('gift');
            break; // show one at a time
          }
        }
        prevBadgesRef.current = curr;
      } catch {}
    };
    checkBadges();
    const id = setInterval(checkBadges, 60000);
    return () => clearInterval(id);
  }, [onboardDone, currentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Freemium gate: show upgrade sheet when 7-day trial expires ────────────
  useEffect(() => {
    if (!onboardDone || !signedUpAt) return;
    if (license && license !== 'freemium') return;  // already paid
    if (businessId) return; // business team members are exempt
    const daysSince = (Date.now() - new Date(signedUpAt).getTime()) / 86400000;
    const floor = Math.floor(daysSince);
    // Day 7 (0-indexed floor=6): show last-day popup once per calendar day
    if (floor === 6) {
      const today = new Date().toISOString().slice(0, 10);
      const lastShown = localStorage.getItem('sl_trial_lastday_shown');
      if (lastShown !== today) {
        localStorage.setItem('sl_trial_lastday_shown', today);
        setTrialEndVariant('last_day');
        setShowTrialEnd(true);
      }
    }
  }, [onboardDone, signedUpAt, license, businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load pending invite and show modal once onboarding is done ──────────────
  useEffect(() => {
    const raw = localStorage.getItem('sl_pending_invite');
    if (!raw) return;
    try {
      const inv = JSON.parse(raw);
      if (inv?.token) setPendingInvite(inv);
    } catch {}
  }, []);

  useEffect(() => {
    if (!onboardDone || !pendingInvite) return;
    // Fetch invite details then show modal
    fetch(`${BASE}/invites/${pendingInvite.token}`)
      .then(r => r.json())
      .then(data => { setInviteDetails({ ...data, suggestedAction: pendingInvite.action }); setShowInviteModal(true); })
      .catch(() => { /* silently ignore stale/invalid tokens */ localStorage.removeItem('sl_pending_invite'); });
  }, [onboardDone, pendingInvite]);

  // ── Business invite via URL (?bizinvite=TOKEN) ───────────────────
  useEffect(() => {
    if (!onboardDone) return;
    const token = localStorage.getItem('sl_pending_biz_invite');
    if (!token) return;
    fetch(`${BASE}/business-invite/${token}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || data.error || data.status !== 'pending') {
          localStorage.removeItem('sl_pending_biz_invite'); return;
        }
        setBizInvitePopup({ ...data, token });
      })
      .catch(() => localStorage.removeItem('sl_pending_biz_invite'));
  }, [onboardDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Friend invite via URL (?friendinvite=TOKEN) ──────────────────
  useEffect(() => {
    if (!onboardDone) return;
    const token = localStorage.getItem('sl_pending_friend_invite');
    if (!token) return;
    fetch(`${BASE}/friends/invite/${token}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || data.error) { localStorage.removeItem('sl_pending_friend_invite'); return; }
        setFriendInviteModal({ ...data, token });
      })
      .catch(() => localStorage.removeItem('sl_pending_friend_invite'));
  }, [onboardDone]);

  // ── Push notification deep-links: ?go=chat / ?go=community ──────
  useEffect(() => {
    if (!onboardDone) return;
    const params = new URLSearchParams(window.location.search);
    const go = params.get('go');
    if (go === 'chat') {
      setTab(7);
      setCommunityInitTab('chat');
      setTimeout(() => setCommunityInitTab(null), 500);
      window.history.replaceState(null, '', window.location.pathname);
    } else if (go === 'community') {
      setTab(7);
      setCommunityInitTab('timeline');
      setTimeout(() => setCommunityInitTab(null), 500);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [onboardDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Appointment invite via URL (?apptinvite=TOKEN) ───────────────
  useEffect(() => {
    const token = localStorage.getItem('sl_pending_appt_invite');
    if (!token) return;
    fetch(`${BASE}/invites/${token}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data || data.error) { localStorage.removeItem('sl_pending_appt_invite'); return; }
        setApptInviteDetails({ ...data, token });
        setPendingApptInvite(token);
        if (onboardDone) setShowApptInviteScreen(true);
      })
      .catch(() => localStorage.removeItem('sl_pending_appt_invite'));
  }, [onboardDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Game invite via URL (?gameinvite=TOKEN) ──────────────────────
  useEffect(() => {
    const token = localStorage.getItem('sl_pending_game_invite');
    if (!token) return;
    getGameSession(token).then(session => {
      if (!session || session.error) { localStorage.removeItem('sl_pending_game_invite'); return; }
      setPendingGameInvite({ ...session, token });
      // If onboarding already done, launch the game immediately
      if (onboardDone) {
        setTab(3); // Games tab
        localStorage.removeItem('sl_pending_game_invite');
      }
    }).catch(() => localStorage.removeItem('sl_pending_game_invite'));
  }, [onboardDone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll for active game sessions + pending invitations ──────────
  useEffect(() => {
    if (!onboardDone || !currentUserId) return;
    const check = async () => {
      const data = await getActiveSessions(currentUserId);
      // Update active sessions list for dashboard
      setActiveSessions(data.sessions || []);
      // Show popup for first unseen pending invite
      for (const s of (data.pending || [])) {
        if (!seenInviteTokens.current.has(s.token)) {
          seenInviteTokens.current.add(s.token);
          setGameInvitePopup(s);
          break;
        }
      }
    };
    check();
    const id = setInterval(check, 10000);
    return () => clearInterval(id);
  }, [onboardDone, currentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Business wallet balance loader ───────────────────────────────────────
  // DB-only: never writes localStorage — state is the single source of truth.
  const fetchBizWallet = (bizId) => {
    if (!bizId) return;
    fetch(`${BASE}/wallet/${encodeURIComponent('biz_' + bizId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && typeof d.balance === 'number') setBizWalletBalance(d.balance); })
      .catch(() => {});
  };

  // Re-fetch biz wallet whenever businessId is set (DB-first: starts null, set by hydration)
  useEffect(() => {
    if (!businessId) return;
    fetchBizWallet(businessId);
  }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const respondToFriendInvite = async (action) => {
    if (!friendInviteModal) return;
    try {
      await fetch(`${BASE}/friends/invite/${friendInviteModal.token}/respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
    } catch {}
    if (action === 'accept' && friendInviteModal.from_id) {
      setFriends(prev => [...new Set([...prev, friendInviteModal.from_id])]);
    }
    setFriendInviteModal(prev => ({ ...prev, responded: action }));
    localStorage.removeItem('sl_pending_friend_invite');
    setTimeout(() => setFriendInviteModal(null), 2500);
  };

  const respondToInvite = async (action) => {
    if (!inviteDetails) return;
    try {
      await fetch(`${BASE}/invites/${inviteDetails.token}/respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
    } catch {}
    setInviteResponded(action);
    localStorage.removeItem('sl_pending_invite');
    setTimeout(() => { setShowInviteModal(false); setInviteDetails(null); setPendingInvite(null); setInviteResponded(false); }, 2500);
  };

  const todayKey = new Date().toISOString().slice(0,10);
  const autoNavRef = useRef(false);
  const scrollRef = useRef(null);
  const navHistoryRef = useRef([]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [tab]);

  // Track tab history for hardware back button
  useEffect(() => {
    navHistoryRef.current = [...navHistoryRef.current.filter(t => t !== tab), tab];
  }, [tab]);

  // Android hardware back button
  useEffect(() => {
    if (!isNative()) return;
    const handler = CapApp.addListener('backButton', () => {
      // Close overlays first
      if (showNotifs)           { setShowNotifs(false);           return; }
      if (showTips)             { setShowTips(false);             return; }
      if (showSettingsMenu)     { setShowSettingsMenu(false);     return; }
      if (showProfileDropdown)  { setShowProfileDropdown(false);  return; }
      if (profileTarget)    { setProfileTarget(null); setTab(0); return; }
      // Go back in tab history
      const history = navHistoryRef.current;
      if (history.length > 1) {
        const prev = history[history.length - 2];
        navHistoryRef.current = history.slice(0, -1);
        setTab(prev);
      } else {
        // At root — exit app
        CapApp.exitApp();
      }
    });
    return () => { handler.then(h => h.remove()); };
  }, [showNotifs, showTips, showSettingsMenu, profileTarget]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!onboardDone || !googleId || autoNavRef.current) return;
    autoNavRef.current = true;
    // Read directly from localStorage so we get the post-hydration value,
    // not the stale React state that was captured before hydrateFromDB ran.
    let freshSleep = [];
    let freshMood  = [];
    try { freshSleep = JSON.parse(localStorage.getItem('sl_sleep_log') || '[]'); } catch {}
    try { freshMood  = JSON.parse(localStorage.getItem('sl_mood_log')   || '[]'); } catch {}
    const sleepDone = freshSleep.some(e => e.date === todayKey);
    const moodDone  = freshMood.some(e  => e.date === todayKey);
    if (!sleepDone || !moodDone) setTab(8);
  }, [onboardDone, googleId]); // eslint-disable-line

  const currentUser   = {id:currentUserId,name:userName||"You",color:myAccentColor,bannerColor:myBannerColor,mood:"good",mentalEnergy:70,physicalEnergy:60,intention:weekIntention,isResting:false,posts:myPosts};
  const allUsers      = [currentUser,...SEED_USERS];

  // ── Pending-item push reminders ──────────────────────────────────
  const _reminderOffset = parseInt(localStorage.getItem('sl_reminder_offset_mins') || '15', 10);
  usePendingReminders({ friendRequests, currentUserId, lang, reminderOffset: _reminderOffset });

  /* ── Sport flow: tab light-up sequence ───────────────────────────────────── */
  useEffect(() => {
    if (sportFlowPhase !== 'tab_light') return;
    const ALL_TABS = [0,1,2,3,4,5,7,8];
    let i = 0;
    const iv = setInterval(() => {
      if (i >= ALL_TABS.length) {
        clearInterval(iv);
        setTimeout(() => setSportFlowPhase('game_prompt'), 500);
        return;
      }
      setLitNavTabs(prev => new Set([...prev, ALL_TABS[i]]));
      i++;
    }, 300);
    return () => clearInterval(iv);
  }, [sportFlowPhase]);

  /* ── Sport flow: dashboard tutorial typing effect ────────────────────────── */
  const DASH_TUT_STEPS = lang === 'nl' ? [
    { title:'🏠 Jouw Dashboard', text:'Dit is het hart van Sense Lifting. Al jouw data, voortgang en inzichten op één plek.' },
    { title:'📊 Sense Score kaarten', text:'Elke kaart meet een pijler van jouw welzijn — slaap, focus, herstel en stemming. Ze worden gevuld naarmate je meer doet.' },
    { title:'⚡ Hoe het werkt', text:'Doe oefeningen, speel games, log je slaap en stemming — elke actie voedt jouw score en helpt jou groeien.' },
    { title:'🎯 Klaar!', text:'Je bent volledig aan boord. Voel vrij om te verkennen — alles staat voor je open. Veel succes!' },
  ] : [
    { title:'🏠 Your Dashboard', text:'This is the heart of Sense Lifting. All your data, progress and insights in one place.' },
    { title:'📊 Sense Score cards', text:'Each card measures a pillar of your wellbeing — sleep, focus, recovery and mood. They fill up as you do more.' },
    { title:'⚡ How it works', text:'Do exercises, play games, log your sleep and mood — every action feeds your score and helps you grow.' },
    { title:'🎯 You\'re set!', text:'You\'re fully on board. Feel free to explore — everything is open for you. Good luck!' },
  ];

  useEffect(() => {
    if (sportFlowPhase !== 'dash_tutorial') return;
    const step = DASH_TUT_STEPS[dashTutStep];
    if (!step) return;
    setTutTyped('');
    let i = 0;
    const full = step.text;
    const iv = setInterval(() => {
      i++;
      setTutTyped(full.slice(0, i));
      if (i >= full.length) clearInterval(iv);
    }, 22);
    return () => clearInterval(iv);
  }, [sportFlowPhase, dashTutStep]); // eslint-disable-line react-hooks/exhaustive-deps

  setThemeGlobal(themeId);
  document.documentElement.dir = isRTL(lang) ? "rtl" : "ltr";
  const handleTheme = id => { setThemeIdRaw(id); setThemeGlobal(id); };
  const resetWeek   = () => { setWeekIntention(""); setWeekAgenda({}); setOnboardDone(false); setOnboardStep("splash"); };

  const sendFriendRequest = async toId => {
    if (friends.includes(toId)) return;
    if (friendRequests.some(r=>r.from===currentUserId&&r.to===toId)) return;
    const reqId = Date.now();
    setFriendRequests(prev=>[...prev,{id:reqId,from:currentUserId,to:toId,status:"pending"}]);
    const token = await syncFriendRequest(toId);
    if (token) {
      setFriendRequests(prev=>prev.map(r=>r.id===reqId?{...r,invite_token:token}:r));
    }
    notifyUser(toId, 'friend_request', currentUserId,
      lang==='nl'
        ? `${userName||'Iemand'} wil je toevoegen als vriend`
        : `${userName||'Someone'} sent you a friend request`
    );
  };
  const acceptFriend = fromId => {
    setFriends(prev=>[...new Set([...prev,fromId])]);
    setFriendRequests(prev=>prev.map(r=>r.from===fromId&&r.to===currentUserId?{...r,status:"accepted"}:r));
    setNotifications(prev=>[{id:Date.now(),type:"friend_accepted",from:fromId,text:lang==='nl'?`Vriendschapsverzoek geaccepteerd`:`Friend request accepted`,time:"just now",read:false},...prev]);
    syncFriendAccept(fromId);
    trackSocialEvent('friend_accept', { targetId: fromId, weight: 1 });
    notifyUser(fromId, 'friend_accepted', userName||currentUserId,
      lang==='nl'
        ? `${userName||'Iemand'} heeft je vriendschapsverzoek geaccepteerd`
        : `${userName||'Someone'} accepted your friend request`
    );
  };
  const declineFriend = fromId => {
    setFriendRequests(prev=>prev.map(r=>r.from===fromId&&r.to===currentUserId?{...r,status:"declined"}:r));
    syncFriendDecline(fromId);
    notifyUser(fromId, 'friend_declined', userName||currentUserId,
      lang==='nl'
        ? `${userName||'Iemand'} heeft je vriendschapsverzoek afgewezen`
        : `${userName||'Someone'} declined your friend request`
    );
  };
  const openProfile      = u => { setProfileTarget(u); setTab(99); };
  const addNotification  = n => setNotifications(prev => {
    const fp = `${n.type}|${n.text}`;
    if (prev.some(p => `${p.type}|${p.text}` === fp)) return prev;
    return [{id: Date.now(), ...n, read: false}, ...prev];
  });

  // ── Public business page (no auth required) ──────────────────────
  // Support both ?bizpage=ID URL param (direct link) and in-app overlay
  const bizPageParam = new URLSearchParams(window.location.search).get('bizpage');
  if (bizPageParam) {
    return <BusinessPublicPage businessId={bizPageParam} lang={lang} onBack={null} />;
  }
  // Exclude chat_message type — those are already counted in the community button badge
  const unreadCount = notifications.filter(n=>!n.read && n.type !== 'chat_message').length;
  const globalBg    = `linear-gradient(160deg,${_T.bg} 0%,${_T.bg2} 100%)`;
  const langMeta    = LANGUAGES.find(l=>l.code===lang) || LANGUAGES[0];
  const LangBtn     = () => (<button style={s_GB({fontSize:11,padding:"4px 12px"})} onClick={()=>setShowLangPicker(true)}>{langMeta.flag} {langMeta.code.toUpperCase()}</button>);
  const LangOverlay = () => (<><div style={{position:"fixed",top:14,right:14,zIndex:199}}><LangBtn/></div>{showLangPicker&&<LanguagePicker onClose={()=>setShowLangPicker(false)} lang={lang} onSelect={code=>{setLang(code);document.documentElement.dir=isRTL(code)?"rtl":"ltr";}}/>}</>);

  // ── Profile page (full-screen overlay) ──────────────────────────
  if (tab===99 && profileTarget) {
    return (
      <div style={{background:globalBg,minHeight:"100vh"}}>
        <style>{CSS}</style>
        <ProfilePage
          user={profileTarget} lang={lang} currentUserId={currentUserId}
          friends={friends} friendRequests={friendRequests}
          onFriendRequest={sendFriendRequest}
          onBack={()=>{setProfileTarget(null);setTab(0);}}
          onOpenChat={()=>{setProfileTarget(null);setTab(7);}}
          onAcceptFriend={acceptFriend} onDeclineFriend={declineFriend}
          myPosts={myPosts}
          onPlayTogether={u=>{setGamePresetPartner(u);setProfileTarget(null);setTab(3);}}
          onViewBizPage={id=>{setViewBizPageId(id);}}
        />
      </div>
    );
  }

  // ── Guest session expired ────────────────────────────────────────
  if (guestExpired) {
    return (
      <div style={{ background: globalBg, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
        <style>{CSS}</style>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: '#fff', marginBottom: 12 }}>
          {nl ? 'Gastperiode verlopen' : 'Guest session expired'}
        </div>
        <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 28, maxWidth: 300, lineHeight: 1.6 }}>
          {nl
            ? 'Je gratis gastperiode van 1 week is verlopen. Meld je aan met Google om je voortgang op te slaan en nooit meer data te verliezen.'
            : 'Your free 1-week guest period has expired. Sign in with Google to save your progress and never lose data again.'}
        </div>
        <button
          style={{ background: 'linear-gradient(135deg,#C8A84B,#e6c96b)', color: '#0d1117', border: 'none', borderRadius: 14, padding: '14px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
          onClick={() => { setGuestExpired(false); setOnboardDone(false); setOnboardStep('googleauth'); }}
        >
          {nl ? '🔑 Aanmelden' : '🔑 Sign in'}
        </button>
      </div>
    );
  }

  // ── Onboarding flow ──────────────────────────────────────────────
  if (!onboardDone) {
    // During post-login onboarding: show games full-screen with a back button
    if (onboardGamesOpen && currentUserId) {
      return (
        <div style={{background:globalBg,minHeight:'100vh',position:'relative'}}>
          <style>{CSS}</style>
          <button onClick={() => setOnboardGamesOpen(false)} style={{position:'fixed',top:16,left:16,zIndex:9999,background:'rgba(0,0,0,0.6)',border:'1px solid #2a3a52',borderRadius:50,padding:'8px 16px',color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer'}}>
            ← {nl ? 'Terug' : 'Back'}
          </button>
          <GamesScreen lang={lang} allUsers={allUsers} friends={friends} currentUserId={currentUserId} userName={userName} license={license} walletBalance={businessId ? bizWalletBalance : walletBalance} onSpendCredit={handleSpendCredit}/>
        </div>
      );
    }
    // Floating games button shown on post-login steps
    const gamesBtn = currentUserId ? (
      <button onClick={() => setOnboardGamesOpen(true)} style={{position:'fixed',bottom:24,right:20,zIndex:9000,background:'linear-gradient(135deg,#7B6FD0,#5B4FC0)',border:'none',borderRadius:50,padding:'10px 18px',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',boxShadow:'0 4px 16px rgba(123,111,208,0.4)',display:'flex',alignItems:'center',gap:6}}>
        🎮 {nl ? 'Spellen' : 'Games'}
      </button>
    ) : null;

    // Game invitees skip splash + slides — they only need to sign in/up
    const hasGameInvite = !!localStorage.getItem('sl_pending_game_invite') || !!pendingGameInvite;
    if (onboardStep==="splash") {
      if (hasGameInvite) { setOnboardStep("googleauth"); return null; }
      return <><LangOverlay/><SplashScreen onNext={()=>setOnboardStep("googleauth")} themeId={themeId} lang={lang}/></>;
    }
    if (onboardStep==="slides")
      return <><LangOverlay/><div style={{background:globalBg,minHeight:"100vh"}}><style>{CSS}</style><OnboardSlides lang={lang} onDone={()=>setOnboardStep("googleauth")}/></div></>;
    if (onboardStep==="googleauth") {
      const resetToken = new URLSearchParams(window.location.search).get('reset');
      const initialAuthMode = resetToken ? 'reset' : undefined;
      return <><LangOverlay/><GoogleAuthScreen lang={lang} initialMode={initialAuthMode} onDone={async userData=>{
        if(userData?.name){setGoogleName(userData.name);}
        if(userData?.picture){setGooglePicture(userData.picture);}
        // For email auth: sl_user_id already set — just hydrate existing data.
        // For Google auth: create/find user + hydrate.
        // For guest: skip initUser entirely.
        if(userData){
          let contactInviteToken;
          try { contactInviteToken = JSON.parse(localStorage.getItem('sl_contact_invite') || 'null')?.token; } catch {}
          await initUser(userData.emailAuth
            ? { name: userData.name }
            : { googleId:userData.googleId, googleEmail:userData.email, googlePicture:userData.picture, name:userData.name, inviteToken: contactInviteToken || undefined }
          );
          // Clear the invite so it can't be applied twice
          if (!userData.emailAuth && contactInviteToken) localStorage.removeItem('sl_contact_invite');
        }
        // ── If this account already completed onboarding (hydrated from DB), skip straight to app ──
        const alreadyDone = JSON.parse(localStorage.getItem('sl_onboard_done') || 'false');
        if (alreadyDone) {
          try {
            const n = localStorage.getItem('sl_name'); if(n){const v=JSON.parse(n);if(v)setUserName(v);}
            const pic = localStorage.getItem('sl_google_picture'); if(pic)setGooglePicture(pic);
            const age = JSON.parse(localStorage.getItem('sl_age')||'null'); if(age)setUserAge(age);
            const h = localStorage.getItem('sl_notifs'); if(h){const v=JSON.parse(h);if(Array.isArray(v))setNotifications(v);}
            const r = localStorage.getItem('sl_friend_requests'); if(r){const v=JSON.parse(r);if(Array.isArray(v))setFriendRequests(v);}
            const f = localStorage.getItem('sl_friends'); if(f){const v=JSON.parse(f);if(Array.isArray(v))setFriends(v);}
            const p = localStorage.getItem('sl_my_posts'); if(p){const v=JSON.parse(p);if(Array.isArray(v))setMyPosts(v);}
            const s = localStorage.getItem('sl_signed_up'); if(s){const v=JSON.parse(s);if(v)setSignedUpAt(v);}
            const l = localStorage.getItem('sl_license'); if(l){const v=JSON.parse(l);if(v)setLicense(v);}
            const th = localStorage.getItem('sl_theme'); if(th){const v=JSON.parse(th);if(v){setThemeIdRaw(v);setThemeGlobal(v);}}
            const bc = localStorage.getItem('sl_banner_color'); if(bc){const v=JSON.parse(bc);if(v)setMyBannerColor(v);}
            const ac = localStorage.getItem('sl_accent_color'); if(ac){const v=JSON.parse(ac);if(v)setMyAccentColor(v);}
            // Hydrate intention, admin/researcher rights, wallet, and business data
            const hydInt = localStorage.getItem('sl_intention'); if(hydInt){const v=JSON.parse(hydInt);if(v)setWeekIntention(v);}
            const hydAdmin2 = localStorage.getItem('sl_is_admin'); if(hydAdmin2){try{setIsAdmin(JSON.parse(hydAdmin2));}catch{}}
            const hydRes2 = localStorage.getItem('sl_is_researcher'); if(hydRes2){try{setIsResearcher(JSON.parse(hydRes2));}catch{}}
            const hydWal2 = localStorage.getItem('sl_wallet_balance'); if(hydWal2){try{setWalletBalance(parseInt(hydWal2,10));}catch{}}
          } catch {}
          setGoogleId(localStorage.getItem('sl_google_id') || (userData?.emailAuth ? 'email_auth' : (userData?.googleId || 'guest')));
          setOnboardDone(true);
          // initUser above already wrote sl_business_id + sl_businesses to localStorage.
          // Read them directly so the correct active business is set without any
          // switchBusiness(safe[0]) race condition overwriting the user's choice.
          {
            const hydBizId      = localStorage.getItem('sl_business_id'); // raw string
            const hydBizListRaw = localStorage.getItem('sl_businesses');
            let hydBizList = null;
            try { hydBizList = hydBizListRaw ? JSON.parse(hydBizListRaw) : null; } catch {}
            if (Array.isArray(hydBizList) && hydBizList.length > 0) {
              setBusinesses(hydBizList);
              const activeBizId = hydBizId || hydBizList[0]?.id;
              if (activeBizId) {
                const activeBizRow = hydBizList.find(b => b.id === activeBizId) || hydBizList[0];
                setBusinessId(activeBizId);
                setBizDisplayName(activeBizRow?.name || '');
                setBizDisplayLogo(activeBizRow?.logo_url || '');
              }
            } else {
              // No cached businesses yet — fetch fresh, passing the stored activeId so
              // loadMyBusinesses won't blindly call switchBusiness(safe[0]).
              const uidB = localStorage.getItem('sl_user_id');
              if (uidB) loadMyBusinesses(uidB, hydBizId || null);
            }
          }
          return;
        }
        // ── New user with appointment invite: skip onboarding, go to invite screen ──
        const hasPendingAppt = !!localStorage.getItem('sl_pending_appt_invite');
        if (hasPendingAppt) {
          try{const pic=localStorage.getItem('sl_google_picture');if(pic)setGooglePicture(pic);}catch{}
          const gid = userData ? (localStorage.getItem('sl_google_id') || userData.googleId || 'email_auth') : 'guest';
          setGoogleId(gid);
          setOnboardDone(true);
          setShowApptInviteScreen(true);
          return;
        }
        // ── New user with game invite: skip onboarding, go straight to app ──
        const hasPendingGame = !!localStorage.getItem('sl_pending_game_invite');
        if (hasPendingGame) {
          try{const pic=localStorage.getItem('sl_google_picture');if(pic)setGooglePicture(pic);}catch{}
          const gid = userData ? (localStorage.getItem('sl_google_id') || userData.googleId || 'email_auth') : 'guest';
          setGoogleId(gid);
          setOnboardDone(true);
          setTab(3); // Games tab
          return;
        }
        // ── New user with business invite: skip onboarding, go straight to app + show join popup ──
        const hasPendingBizInvite = !!localStorage.getItem('sl_pending_biz_invite');
        if (hasPendingBizInvite) {
          try{const pic=localStorage.getItem('sl_google_picture');if(pic)setGooglePicture(pic);}catch{}
          const gid = userData ? (localStorage.getItem('sl_google_id') || userData.googleId || 'email_auth') : 'guest';
          setGoogleId(gid);
          setOnboardDone(true);
          return;
        }
        // ── New user: continue full onboarding ──
        try{const pic=localStorage.getItem('sl_google_picture');if(pic)setGooglePicture(pic);}catch{}
        setGoogleId(userData ? (localStorage.getItem('sl_google_id') || userData.googleId || 'email_auth') : 'guest');
        setOnboardStep("nameage");
      }}/></>;
    }
    if (onboardStep==="nameage")
      return <><LangOverlay/><div style={{background:globalBg,minHeight:"100vh"}}><style>{CSS}</style><NameAgeScreen lang={lang} initialName={googleName} onDone={(n,age)=>{setUserName(n);setUserAge(age);setOnboardStep("sport_onboard");}}/></div>{gamesBtn}</>;

    if (onboardStep==="sport_onboard")
      return <SportOnboard lang={lang} onDone={()=>{
        try { const gid=localStorage.getItem('sl_google_id'); if(gid) localStorage.setItem(`sl_onboard_${gid}`,'1'); } catch {}
        if (!signedUpAt) setSignedUpAt(new Date().toISOString().slice(0,10));
        setOnboardDone(true);
        setTab(3); // games tab
        setSportFlowPhase('video_intro');
        setLitNavTabs(new Set());
      }}/>;
  }

  // ── Re-auth: onboarding done but logged out (no Google session) ──
  if (onboardDone && !googleId) {
    return (
      <><LangOverlay/>
      <div style={{background:globalBg,minHeight:"100vh"}}>
        <style>{CSS}</style>
        <GoogleAuthScreen lang={lang} onDone={async userData=>{
          if(userData){
            await initUser(userData.emailAuth
              ? { name: userData.name }
              : { googleId:userData.googleId, googleEmail:userData.email, googlePicture:userData.picture, name:userData.name }
            );
          }
          // Restore hydrated state after re-auth
          try {
            if(userData?.picture) setGooglePicture(userData.picture);
            const pic = localStorage.getItem('sl_google_picture'); if(pic) setGooglePicture(pic);
            const n = localStorage.getItem('sl_name'); if(n){const v=JSON.parse(n);if(v)setUserName(v);}
            const age = JSON.parse(localStorage.getItem('sl_age')||'null'); if(age) setUserAge(age);
            const h = localStorage.getItem('sl_notifs'); if(h){const v=JSON.parse(h);if(Array.isArray(v))setNotifications(v);}
            const r = localStorage.getItem('sl_friend_requests'); if(r){const v=JSON.parse(r);if(Array.isArray(v))setFriendRequests(v);}
            const f = localStorage.getItem('sl_friends'); if(f){const v=JSON.parse(f);if(Array.isArray(v))setFriends(v);}
            const p = localStorage.getItem('sl_my_posts'); if(p){const v=JSON.parse(p);if(Array.isArray(v))setMyPosts(v);}
            const s = localStorage.getItem('sl_signed_up'); if(s){const v=JSON.parse(s);if(v)setSignedUpAt(v);}
            const l = localStorage.getItem('sl_license'); if(l){const v=JSON.parse(l);if(v)setLicense(v);}
            const th = localStorage.getItem('sl_theme'); if(th){const v=JSON.parse(th);if(v){setThemeIdRaw(v);setThemeGlobal(v);}}
            const bc = localStorage.getItem('sl_banner_color'); if(bc){const v=JSON.parse(bc);if(v)setMyBannerColor(v);}
            const ac = localStorage.getItem('sl_accent_color'); if(ac){const v=JSON.parse(ac);if(v)setMyAccentColor(v);}
            const hydInt2 = localStorage.getItem('sl_intention'); if(hydInt2){const v=JSON.parse(hydInt2);if(v)setWeekIntention(v);}
          } catch {}
          // Re-subscribe to push under the (now correct) user ID after re-auth.
          // Without this, the subscription stays under the old anonymous ID and
          // chat/feed pushes addressed to the real ID find zero subscriptions.
          subscribeToPush();
          // initUser above wrote sl_business_id + sl_businesses to localStorage.
          // Restore business state directly to avoid switchBusiness(safe[0]) race condition.
          {
            const hydBizId      = localStorage.getItem('sl_business_id');
            const hydBizListRaw = localStorage.getItem('sl_businesses');
            let hydBizList = null;
            try { hydBizList = hydBizListRaw ? JSON.parse(hydBizListRaw) : null; } catch {}
            if (Array.isArray(hydBizList) && hydBizList.length > 0) {
              setBusinesses(hydBizList);
              const activeBizId = hydBizId || hydBizList[0]?.id;
              if (activeBizId) {
                const activeBizRow = hydBizList.find(b => b.id === activeBizId) || hydBizList[0];
                setBusinessId(activeBizId);
                setBizDisplayName(activeBizRow?.name || '');
                setBizDisplayLogo(activeBizRow?.logo_url || '');
              }
            } else {
              const uidReauth = localStorage.getItem('sl_user_id');
              if (uidReauth) loadMyBusinesses(uidReauth, hydBizId || null);
            }
          }
          setGoogleId(localStorage.getItem('sl_google_id') || (userData?.emailAuth ? 'email_auth' : (userData?.googleId || 'guest')));
        }}/>
      </div></>
    );
  }

  // ── Appointment invite screen (new users or existing users with pending invite) ──
  const apptInviteHandlers = {
    onAccept: (inv) => {
      if (inv.appointment_date) {
        const item = { id: Date.now(), type:'custom', icon:'📌', label:inv.title, labelNL:inv.title, startTime:inv.start||inv.start_time||'', endTime:inv.end||inv.end_time||'', duration:60, mental:10, physical:5, description:inv.description||'', attendees:[] };
        setDatedAgenda(prev => ({ ...prev, [inv.appointment_date]: [...((prev||{})[inv.appointment_date]||[]), item] }));
      }
      fetch(`${BASE}/invites/${inv.token}/respond`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ action:'accept', responder_name:userName||'Guest', responder_id:currentUserId }) }).catch(()=>{});
      localStorage.removeItem('sl_pending_appt_invite');
      setShowApptInviteScreen(false); setApptInviteDetails(null); setPendingApptInvite(null);
    },
    onDecline: (inv) => {
      if (!inv.skip) fetch(`${BASE}/invites/${inv.token}/respond`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ action:'decline', responder_name:userName||'Guest', responder_id:currentUserId }) }).catch(()=>{});
      localStorage.removeItem('sl_pending_appt_invite');
      setShowApptInviteScreen(false); setApptInviteDetails(null); setPendingApptInvite(null);
    },
  };
  if (showApptInviteScreen && apptInviteDetails) {
    return (
      <ErrorBoundary>
        <style>{CSS}</style>
        <AppointmentInviteScreen lang={lang} invite={apptInviteDetails} userName={userName} {...apptInviteHandlers}/>
      </ErrorBoundary>
    );
  }

  // ── Main app ─────────────────────────────────────────────────────
  const incoming   = (friendRequests||[]).filter(r=>r.to===currentUserId&&r.status==="pending");
  const PAID_INDIVIDUAL = ['starter','athlete','sensei','professional'];
  const hasWallet = PAID_INDIVIDUAL.includes(license);
  const NAV        = [
    {icon:"🧘", label:t("nav.sessions",lang), tab:2},
    {icon:"🏋️", label:t("nav.training",lang), tab:3},
    {icon:"🦋", label:"Coach",             tab:4},
    {icon:"🎯", label:t("nav.goals",lang), tab:5},
    {icon:"😴", label:t("nav.sleep",lang), tab:8},
    ...(hasWallet    ? [{icon:"💰", label:lang==='nl'?'Wallet':'Wallet', tab:13}] : []),
    ...(isResearcher ? [{icon:"🔬", label:"Lab",    tab:12}] : []),
    ...(isAdmin      ? [{icon:"🏢", label:"Office", tab:11}] : []),
  ];
  const sharedProps = {
    lang, weekIntention, setWeekIntention, weekAgenda, setWeekAgenda,
    userName, currentUserId, allUsers,
    friends, friendRequests,
    onFriendRequest:sendFriendRequest, onAcceptFriend:acceptFriend, onDeclineFriend:declineFriend,
    conversations, setConversations, myPosts, setMyPosts,
  };
  // ── Load all businesses this user belongs to ─────────────────────────────
  // activeId is passed explicitly to avoid stale React closure (state updates are async)
  // Declared as a function (not const arrow) so it is hoisted and usable before this line.
  function loadMyBusinesses(uid, activeId) {
    if (!uid) return;
    fetch(`${BASE}/businesses/mine?uid=${uid}`)
      .then(r => r.json())
      .then(list => {
        if (!Array.isArray(list) || list.length === 0) return;
        const safe = list.filter(b => b && b.id);
        // Only update the businesses list — never touch businessId, displayName,
        // displayLogo or wallet here. Those are owned by switchBusiness and the
        // businessId useEffect so stale async responses can't overwrite them.
        setBusinesses(safe);
        if (!activeId) {
          // No active business at all — default to first in list
          switchBusiness(safe[0]);
        }
      })
      .catch(() => {});
  }

  function switchBusiness(biz) {
    if (!biz?.id) return;
    setBizWalletBalance(0); // clear stale balance immediately
    setBusinessId(biz.id);
    setBizDisplayName(biz.name || '');
    setBizDisplayLogo(biz.logo_url || '');
    fetchBizWallet(biz.id);
    // Persist active business to DB
    if (currentUserId) {
      fetch(`${BASE}/businesses/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: currentUserId, business_id: biz.id }),
      }).catch(() => {});
    }
  }

  // ── Credit spending ────────────────────────────────────────────────────────
  // Wallet priority: freemium=free, professional=unlimited, business member=biz pool, else personal.
  async function handleSpendCredit(amount = 1, reason = 'game') {
    // Free tiers
    if (!license || license === 'freemium') return true;
    if (license === 'professional') return true;

    const uid = currentUserId;
    if (!uid) return false;

    // Business member — exclusively use the business wallet pool (no personal fallback)
    if (businessId) {
      try {
        const res = await fetch(`${BASE}/wallet/${encodeURIComponent('biz_' + businessId)}/spend`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount, reason }),
        });
        if (!res.ok) return false; // insufficient biz credits — never touch personal wallet
        const data = await res.json();
        if (data.balance !== undefined) setBizWalletBalance(data.balance);
        return true;
      } catch { return false; }
    }

    // Personal wallet
    if (walletBalance < amount) return false;
    try {
      const res = await fetch(`${BASE}/wallet/${encodeURIComponent(uid)}/spend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, reason }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.balance !== undefined) setWalletBalance(data.balance);
      return true;
    } catch { return false; }
  }

  // Animated transition for personal ↔ business switch
  const navToBiz = (toTab) => {
    if (toTab === tab) return;
    const isBizSwitch = toTab === 10 || tab === 10;
    if (!isBizSwitch) { setTab(toTab); return; }
    // Phase 1: fade out current screen (180ms)
    setBizTransition({ phase: 'out', to: toTab });
    // Phase 2: ripple flash starts + swap tab while screen is invisible
    setTimeout(() => { setBizTransition({ phase: 'flash', to: toTab }); setTab(toTab); }, 180);
    // Phase 3: fade in new screen (ripple still visible, then fades)
    setTimeout(() => setBizTransition({ phase: 'in', to: toTab }), 260);
    // Done
    setTimeout(() => setBizTransition(null), 900);
  };

  const screenMap = {
    0: <Dashboard {...sharedProps} datedAgenda={datedAgenda} onNav={setTab} themeId={themeId} setThemeId={handleTheme} onResetWeek={resetWeek} setLang={setLang} onOpenLangPicker={()=>setShowLangPicker(true)} onOpenProfile={openProfile} myBannerColor={myBannerColor} setMyBannerColor={setMyBannerColor} myAccentColor={myAccentColor} setMyAccentColor={setMyAccentColor} activeSessions={activeSessions} onRejoinSession={s=>{setPendingGameInvite(s);setTab(3);}} onStopSession={async s=>{await stopGameSession(s.token);setActiveSessions(p=>p.filter(x=>x.token!==s.token));}} license={license} walletBalance={walletBalance} bizWalletBalance={bizWalletBalance} businessId={businessId} bizDisplayName={bizDisplayName} bizDisplayLogo={bizDisplayLogo} onOpenBusiness={()=>navToBiz(10)}/>,
    1: <WeekScreen lang={lang} weekIntention={weekIntention} setWeekIntention={setWeekIntention} weekAgenda={weekAgenda} setWeekAgenda={setWeekAgenda} datedAgenda={datedAgenda} setDatedAgenda={setDatedAgenda} currentUserId={currentUserId} userName={userName}/>,
    2: <ExerciseScreen lang={lang} allUsers={allUsers} friends={friends} license={license} walletBalance={businessId ? bizWalletBalance : walletBalance} onSpendCredit={handleSpendCredit}/>,
    3: <GamesScreen lang={lang} allUsers={allUsers} friends={friends} currentUserId={currentUserId} userName={userName} pendingGameInvite={pendingGameInvite} onClearGameInvite={()=>setPendingGameInvite(null)} presetPartner={gamePresetPartner} onClearPresetPartner={()=>setGamePresetPartner(null)} license={license} walletBalance={businessId ? bizWalletBalance : walletBalance} onSpendCredit={handleSpendCredit}/>,
    4: license && license.startsWith('coaches')
      ? <CoachDashboardScreen userId={currentUserId} license={license} lang={lang} businessId={businessId} />
      : <CoachScreen lang={lang} weekIntention={weekIntention} weekAgenda={weekAgenda} setWeekAgenda={setWeekAgenda} currentUserId={currentUserId}/>,
    5: <GoalsScreen lang={lang}/>,
    7: <CommunityScreen {...sharedProps} onAddNotification={addNotification} initialTab={communityInitTab} onPlayTogether={u=>{setGamePresetPartner(u);setTab(3);}} onNav={setTab} onViewBizPage={id=>setViewBizPageId(id)}/>,
    8: <SleepMoodScreen lang={lang}/>,
    9: <GoalStatsScreen lang={lang} weekIntention={weekIntention} onBack={() => setTab(0)} onNav={setTab} datedAgenda={datedAgenda} setDatedAgenda={setDatedAgenda}/>,
    10: <BusinessScreen key={businessId || 'none'} lang={lang} currentUserId={currentUserId} userName={userName} onBack={()=>navToBiz(0)} businessId={businessId} setBusinessId={setBusinessId} license={license} businesses={businesses} onSwitchBusiness={biz=>{switchBusiness(biz);}} onBusinessCreated={biz=>{loadMyBusinesses(currentUserId,biz.id);}} onOpenChat={()=>{setTab(7);setCommunityInitTab('chat');}} onBizLoaded={b=>{if(b&&b.name){setBizDisplayName(b.name);setBizDisplayLogo(b.logo_url||'');setBusinesses(prev=>prev.map(x=>x.id===b.id?{...x,name:b.name,logo_url:b.logo_url}:x));}}}/>,
    11: <AdminDashboardScreen lang={lang} currentUserId={currentUserId} onBack={()=>setTab(0)}/>,
    12: <LabScreen lang={lang} currentUserId={currentUserId} isResearcher={isResearcher} license={license} onBack={()=>setTab(0)}/>,
    13: <WalletScreen lang={lang} currentUserId={currentUserId} walletBalance={walletBalance} setWalletBalance={setWalletBalance} businessId={businessId} bizWalletBalance={bizWalletBalance} setBizWalletBalance={setBizWalletBalance}/>,
  };

  const acceptBizInvite = async () => {
    try {
      const uid = currentUserId;
      const res = await fetch(`${BASE}/business-invite/${bizInvitePopup.token}/accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: uid }),
      });
      if (!res.ok) throw new Error('accept failed');
      const data = await res.json();
      const bizId = data.biz_id || data.business_id;
      if (bizId) {
        setBusinessId(bizId);
        loadMyBusinesses(uid, bizId);
      }
    } catch (e) { console.warn('acceptBizInvite error', e); }
    localStorage.removeItem('sl_pending_biz_invite');
    setBizInvitePopup(prev => ({ ...prev, _accepted: true }));
    setTimeout(() => { setBizInvitePopup(null); navToBiz(10); }, 1800);
  };
  const declineBizInvite = () => {
    localStorage.removeItem('sl_pending_biz_invite');
    setBizInvitePopup(null);
  };

  const androidInstall = async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    setInstallPrompt(false);
  };
  const dismissIos = () => { localStorage.setItem('sl_ios_banner_dismissed','1'); setShowIosBanner(false); };

  const bannerStyle = { position:'fixed', bottom:0, left:0, right:0, zIndex:300, background:'#1a2235', borderTop:'1px solid rgba(200,168,75,0.3)', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, fontFamily:'Inter,system-ui,sans-serif' };
  const dismissBtn  = { background:'none', border:'none', color:'#6b7a99', cursor:'pointer', fontSize:20, lineHeight:1, padding:0 };

  return (
    <ErrorBoundary>
      {/* ── Personal ↔ Business transition overlay ── */}
      <style>{`
        @keyframes bizFlashExpand { 0%{transform:scale(0);opacity:1} 60%{opacity:0.9} 100%{transform:scale(50);opacity:0} }
        @keyframes bizFlashFade { 0%{opacity:0.8} 100%{opacity:0} }
      `}</style>
      {bizTransition && (
        <div style={{position:'fixed',inset:0,zIndex:9999,pointerEvents:'none',overflow:'hidden'}}>
          {bizTransition.phase === 'flash' && (<>
            <div style={{position:'absolute',left:'50%',top:'50%',width:80,height:80,borderRadius:'50%',background:bizTransition.to===10?`radial-gradient(circle,#C8A84B,#2A9E82)`:`radial-gradient(circle,#0a1220,#111e30)`,transformOrigin:'0 0',animation:'bizFlashExpand 0.42s cubic-bezier(0.4,0,0.2,1) forwards'}}/>
            <div style={{position:'absolute',inset:0,background:bizTransition.to===10?'rgba(200,168,75,0.12)':'rgba(10,18,32,0.3)',animation:'bizFlashFade 0.42s ease-out forwards'}}/>
          </>)}
          {bizTransition.phase === 'out' && (
            <div style={{position:'absolute',inset:0,background:bizTransition.to===10?'rgba(200,168,75,0.06)':'rgba(10,18,32,0.08)',animation:'bizFlashFade 0.22s ease-in forwards'}}/>
          )}
        </div>
      )}
      {/* Stripe payment result toast */}
      {paymentToast && (
        <div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',zIndex:9999,background:paymentToast.success?'#1a3a2a':'#3a1a1a',border:`1px solid ${paymentToast.success?'#4AC87A':'#C05050'}`,borderRadius:16,padding:'14px 20px',display:'flex',alignItems:'center',gap:12,boxShadow:'0 8px 32px rgba(0,0,0,0.4)',minWidth:260,maxWidth:360}}>
          <span style={{fontSize:22}}>{paymentToast.success?'🎉':'❌'}</span>
          <div>
            <div style={{color:'#fff',fontWeight:700,fontSize:14}}>{paymentToast.success?(lang==='nl'?'Betaling geslaagd!':'Payment successful!'):(lang==='nl'?'Betaling geannuleerd':'Payment cancelled')}</div>
            {paymentToast.success&&<div style={{color:'#8899BB',fontSize:11,marginTop:2}}>{paymentToast.credits?(paymentToast.isBiz?(lang==='nl'?`+${paymentToast.credits} credits toegevoegd aan de bedrijfspool.`:`+${paymentToast.credits} credits added to the business pool.`):(lang==='nl'?`+${paymentToast.credits} credits toegevoegd aan je wallet.`:`+${paymentToast.credits} credits added to your wallet.`)):(lang==='nl'?'Je licentie wordt bijgewerkt.':'Your license is being updated.')}</div>}
          </div>
          <button onClick={()=>setPaymentToast(null)} style={{background:'none',border:'none',color:'#6b7a99',cursor:'pointer',fontSize:18,marginLeft:'auto',padding:0}}>✕</button>
        </div>
      )}
      {/* Invite response modal */}
      {pendingApptInvite && apptInviteDetails && !showApptInviteScreen && onboardDone && (
        <AppointmentInviteScreen lang={lang} invite={apptInviteDetails} userName={userName} modal={true} {...apptInviteHandlers}/>
      )}
      {showInviteModal && inviteDetails && (
        <div style={{position:'fixed',inset:0,zIndex:9000,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111e30',border:'1px solid #1e3050',borderRadius:20,padding:'28px 24px',maxWidth:360,width:'100%',textAlign:'center',boxShadow:'0 16px 48px rgba(0,0,0,0.6)'}}>
            {inviteResponded ? (
              <>
                <div style={{fontSize:48,marginBottom:12}}>{inviteResponded==='accept'?'✅':'❌'}</div>
                <div style={{color:'#fff',fontSize:17,fontWeight:700}}>
                  {inviteResponded==='accept'
                    ? (lang==='nl'?'Uitnodiging geaccepteerd!':'Invitation accepted!')
                    : (lang==='nl'?'Uitnodiging geweigerd.':'Invitation declined.')}
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:11,color:'#C8A84B',letterSpacing:3,fontWeight:700,marginBottom:10}}>{lang==='nl'?'UITNODIGING':'INVITATION'}</div>
                <div style={{color:'#fff',fontSize:18,fontWeight:700,marginBottom:6}}>{inviteDetails.title}</div>
                <div style={{color:'#8899BB',fontSize:13,marginBottom:4}}>📅 {inviteDetails.day}</div>
                {inviteDetails.start && <div style={{color:'#8899BB',fontSize:13,marginBottom:16}}>⏰ {inviteDetails.start}{inviteDetails.end?` – ${inviteDetails.end}`:''}</div>}
                <p style={{color:'#aac0d8',fontSize:13,lineHeight:1.6,marginBottom:20}}>
                  {lang==='nl'?'Wil je deelnemen aan dit afspraak?':'Would you like to join this appointment?'}
                </p>
                {inviteDetails.suggestedAction ? (
                  <div>
                    <p style={{color:'#C8A84B',fontSize:12,marginBottom:14}}>
                      {lang==='nl'
                        ? `Je had eerder gekozen om te ${inviteDetails.suggestedAction==='accept'?'accepteren':'weigeren'}. Bevestig hieronder.`
                        : `You previously chose to ${inviteDetails.suggestedAction}. Confirm below.`}
                    </p>
                  </div>
                ) : null}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <button onClick={()=>respondToInvite('accept')} style={{background:'linear-gradient(135deg,#2A9E82,#1a8070)',border:'none',borderRadius:12,color:'#fff',fontSize:14,fontWeight:700,padding:'13px 0',cursor:'pointer'}}>
                    ✅ {lang==='nl'?'Accepteren':'Accept'}
                  </button>
                  <button onClick={()=>respondToInvite('decline')} style={{background:'transparent',border:'1px solid #C05050',borderRadius:12,color:'#C05050',fontSize:14,fontWeight:700,padding:'13px 0',cursor:'pointer'}}>
                    ❌ {lang==='nl'?'Weigeren':'Decline'}
                  </button>
                </div>
                <button onClick={()=>{setShowInviteModal(false);}} style={{marginTop:12,background:'none',border:'none',color:'#4a6080',fontSize:12,cursor:'pointer'}}>
                  {lang==='nl'?'Later beslissen':'Decide later'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Game invite popup — shown when another player invites this user while online */}
      {gameInvitePopup && (
        <div style={{position:'fixed',inset:0,zIndex:9000,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
          ref={el => { if (el) markGameInviteSeen(gameInvitePopup.token); }}
        >
          <div className="popIn" style={{background:'#111e30',border:'1px solid #1e3050',borderRadius:20,padding:'28px 24px',maxWidth:340,width:'100%',textAlign:'center',boxShadow:'0 16px 48px rgba(0,0,0,0.6)'}}>
            <div style={{fontSize:48,marginBottom:10}}>🎮</div>
            <div style={{fontSize:11,color:'#C8A84B',letterSpacing:3,fontWeight:700,marginBottom:8}}>{lang==='nl'?'SPELUITNODIGING':'GAME INVITE'}</div>
            <div style={{color:'#fff',fontSize:17,fontWeight:700,marginBottom:6}}>{gameInvitePopup.inviterName}</div>
            <div style={{color:'#8899BB',fontSize:13,marginBottom:18}}>
              {lang==='nl'
                ? `nodigt je uit voor ${gameInvitePopup.gameTitle}`
                : `invited you to play ${gameInvitePopup.gameTitle}`}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
              <button
                onClick={() => {
                  setPendingGameInvite({ ...gameInvitePopup });
                  setTab(3);
                  setGameInvitePopup(null);
                }}
                style={{background:'linear-gradient(135deg,#2A9E82,#1a8070)',border:'none',borderRadius:12,color:'#fff',fontSize:14,fontWeight:700,padding:'13px 0',cursor:'pointer'}}
              >
                🎮 {lang==='nl'?'Deelnemen':'Join'}
              </button>
              <button
                onClick={() => { declineGameInvite(gameInvitePopup.token); setGameInvitePopup(null); }}
                style={{background:'transparent',border:'1px solid #374151',borderRadius:12,color:'#6b7280',fontSize:14,fontWeight:700,padding:'13px 0',cursor:'pointer'}}
              >
                {lang==='nl'?'Niet nu':'Not now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Friend invite modal */}
      {friendInviteModal && (
        <div style={{position:'fixed',inset:0,zIndex:9000,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111e30',border:'1px solid #1e3050',borderRadius:20,padding:'28px 24px',maxWidth:360,width:'100%',textAlign:'center',boxShadow:'0 16px 48px rgba(0,0,0,0.6)'}}>
            {friendInviteModal.responded ? (
              <>
                <div style={{fontSize:48,marginBottom:12}}>{friendInviteModal.responded==='accept'?'✅':'❌'}</div>
                <div style={{color:'#fff',fontSize:17,fontWeight:700}}>
                  {friendInviteModal.responded==='accept'
                    ? (lang==='nl'?'Vriendschap geaccepteerd!':'Friendship accepted!')
                    : (lang==='nl'?'Verzoek afgewezen.':'Request declined.')}
                </div>
              </>
            ) : friendInviteModal.status !== 'pending' ? (
              <>
                <div style={{fontSize:48,marginBottom:12}}>ℹ️</div>
                <div style={{color:'#fff',fontSize:15,fontWeight:700,marginBottom:8}}>
                  {lang==='nl'?'Dit verzoek is al beantwoord.':'This request has already been answered.'}
                </div>
                <button onClick={()=>setFriendInviteModal(null)} style={{marginTop:12,background:'none',border:'1px solid #374151',color:'#6b7280',borderRadius:10,padding:'10px 20px',fontSize:13,cursor:'pointer'}}>OK</button>
              </>
            ) : (
              <>
                <div style={{fontSize:11,color:'#C8A84B',letterSpacing:3,fontWeight:700,marginBottom:10}}>{lang==='nl'?'VRIENDSCHAPSVERZOEK':'FRIEND REQUEST'}</div>
                <div style={{fontSize:48,marginBottom:12}}>👋</div>
                <div style={{color:'#fff',fontSize:17,fontWeight:700,marginBottom:6}}>{friendInviteModal.from_name}</div>
                <p style={{color:'#aac0d8',fontSize:13,lineHeight:1.6,marginBottom:20}}>
                  {lang==='nl'
                    ? `${friendInviteModal.from_name} wil je toevoegen als vriend op Sense Lifting.`
                    : `${friendInviteModal.from_name} wants to add you as a friend on Sense Lifting.`}
                </p>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <button onClick={()=>respondToFriendInvite('accept')} style={{background:'linear-gradient(135deg,#2A9E82,#1a8070)',border:'none',borderRadius:12,color:'#fff',fontSize:14,fontWeight:700,padding:'13px 0',cursor:'pointer'}}>
                    ✅ {lang==='nl'?'Accepteren':'Accept'}
                  </button>
                  <button onClick={()=>respondToFriendInvite('decline')} style={{background:'transparent',border:'1px solid #C05050',borderRadius:12,color:'#C05050',fontSize:14,fontWeight:700,padding:'13px 0',cursor:'pointer'}}>
                    ❌ {lang==='nl'?'Weigeren':'Decline'}
                  </button>
                </div>
                <button onClick={()=>{setFriendInviteModal(null);localStorage.removeItem('sl_pending_friend_invite');}} style={{marginTop:12,background:'none',border:'none',color:'#4a6080',fontSize:12,cursor:'pointer'}}>
                  {lang==='nl'?'Later beslissen':'Decide later'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Business invite popup */}
      {bizInvitePopup && (
        <div style={{position:'fixed',inset:0,zIndex:9000,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111e30',border:'1px solid #1e3050',borderRadius:20,padding:'28px 24px',maxWidth:360,width:'100%',textAlign:'center',boxShadow:'0 16px 48px rgba(0,0,0,0.6)'}}>
            {bizInvitePopup._accepted ? (
              <>
                <div style={{fontSize:48,marginBottom:12}}>🎉</div>
                <div style={{color:'#fff',fontSize:17,fontWeight:700}}>
                  {nl ? 'Welkom bij het team!' : 'Welcome to the team!'}
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:11,color:'#C8A84B',letterSpacing:3,fontWeight:700,marginBottom:12}}>
                  {nl ? 'TEAM UITNODIGING' : 'TEAM INVITATION'}
                </div>
                {bizInvitePopup.logo_url
                  ? <img src={bizInvitePopup.logo_url} alt="" style={{width:64,height:64,borderRadius:14,objectFit:'cover',marginBottom:12}}/>
                  : <div style={{fontSize:48,marginBottom:12}}>🏢</div>
                }
                <div style={{color:'#fff',fontSize:19,fontWeight:700,marginBottom:4}}>{bizInvitePopup.biz_name}</div>
                <div style={{color:'#8899BB',fontSize:13,marginBottom:4}}>
                  {nl
                    ? `${bizInvitePopup.inviter_name || 'Iemand'} nodigt je uit om lid te worden`
                    : `${bizInvitePopup.inviter_name || 'Someone'} invited you to join`}
                </div>
                <div style={{display:'inline-block',background:'#1e3050',borderRadius:8,padding:'3px 10px',fontSize:12,color:'#C8A84B',fontWeight:600,marginBottom:20}}>
                  {(bizInvitePopup.role||'member').charAt(0).toUpperCase()+(bizInvitePopup.role||'member').slice(1)}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
                  <button onClick={acceptBizInvite} style={{background:'linear-gradient(135deg,#2A9E82,#1a8070)',border:'none',borderRadius:12,color:'#fff',fontSize:14,fontWeight:700,padding:'13px 0',cursor:'pointer'}}>
                    ✅ {nl ? 'Deelnemen' : 'Join'}
                  </button>
                  <button onClick={declineBizInvite} style={{background:'transparent',border:'1px solid #374151',borderRadius:12,color:'#6b7280',fontSize:14,fontWeight:700,padding:'13px 0',cursor:'pointer'}}>
                    {nl ? 'Niet nu' : 'Not now'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* New chat message toast */}
      {newMsgToast && (
        <div
          onClick={() => { setTab(7); setCommunityInitTab('chat'); setTimeout(() => setCommunityInitTab(null), 500); setNewMsgToast(null); }}
          style={{position:'fixed',bottom:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#1e3050',border:'1px solid #2A9E82',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap',animation:'slideUp 0.3s ease'}}
        >
          <span style={{fontSize:18}}>💬</span>
          <span style={{color:'#fff',fontSize:14,fontWeight:600}}>
            {nl ? `Nieuw bericht van ${newMsgToast.name}` : `New message from ${newMsgToast.name}`}
          </span>
        </div>
      )}

      {/* Friend online toast */}
      {friendOnlineToast && (
        <div
          onClick={() => { setTab(7); setFriendOnlineToast(null); }}
          style={{position:'fixed',bottom:newMsgToast ? 148 : 90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#1e3050',border:'1px solid #4CAF50',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}
        >
          <span style={{fontSize:12,color:'#4CAF50'}}>●</span>
          <span style={{color:'#fff',fontSize:14,fontWeight:600}}>
            {nl ? `${friendOnlineToast.name} is online` : `${friendOnlineToast.name} is online`}
          </span>
        </div>
      )}

      {/* Badge unlock modal */}
      {badgeUnlock && (
        <BadgeUnlockModal badge={badgeUnlock} lang={lang} onClose={() => setBadgeUnlock(null)} />
      )}

      {/* Game level-up popup */}
      {gameLevelUp && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(0,0,0,0.75)' }}>
          <div className="popIn" style={{ background: _T.bg2, border: '2px solid #7B6FD0', borderRadius: 28, padding: 28, maxWidth: 320, width: '100%', textAlign: 'center', position: 'relative' }}>
            <button onClick={() => setGameLevelUp(null)} style={{ position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none', color: C.silver, fontSize: 20, cursor: 'pointer', padding: 4, lineHeight: 1 }}>✕</button>
            <div style={{ fontSize: 52, marginBottom: 8 }}>🎮</div>
            <div style={{ fontSize: 11, color: '#7B6FD0', letterSpacing: 3, fontWeight: 700, marginBottom: 6 }}>{nl ? 'LEVEL UP!' : 'LEVEL UP!'}</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: C.white, marginBottom: 8 }}>
              {nl ? `Game Level ${gameLevelUp.newLevel}` : `Game Level ${gameLevelUp.newLevel}`}
            </div>
            <div style={{ color: C.silver, fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
              {nl
                ? `Je bent gestegen van level ${gameLevelUp.oldLevel} naar level ${gameLevelUp.newLevel}! Blijf spelen om je Game Master badge te verdienen.`
                : `You levelled up from ${gameLevelUp.oldLevel} to ${gameLevelUp.newLevel}! Keep playing to earn your Game Master badge.`}
            </div>
            <button style={s_GB({ width: '100%', fontSize: 15 })} onClick={() => setGameLevelUp(null)}>
              {nl ? 'Doorgaan 🎯' : 'Keep playing 🎯'}
            </button>
          </div>
        </div>
      )}

      {/* Theme unlock popup */}
      {themeUnlock && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(0,0,0,0.82)' }}>
          <div className="popIn" style={{ background: _T.bg2, border: '2px solid #C8A84B', borderRadius: 28, padding: 28, maxWidth: 320, width: '100%', textAlign: 'center', position: 'relative' }}>
            <button onClick={() => setThemeUnlock(null)} style={{ position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none', color: C.silver, fontSize: 20, cursor: 'pointer', padding: 4, lineHeight: 1 }}>✕</button>
            <div style={{ fontSize: 52, marginBottom: 8 }}>🎨</div>
            <div style={{ fontSize: 11, color: '#C8A84B', letterSpacing: 3, fontWeight: 700, marginBottom: 6 }}>{nl ? 'THEMA ONTGRENDELD!' : 'THEME UNLOCKED!'}</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: C.white, marginBottom: 8, textTransform: 'capitalize' }}>
              {themeUnlock.themeId}
            </div>
            <div style={{ color: C.silver, fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
              {nl
                ? `Je hebt game level ${themeUnlock.level} bereikt en het ${themeUnlock.themeId} thema ontgrendeld! Pas het aan in je profiel.`
                : `You reached game level ${themeUnlock.level} and unlocked the ${themeUnlock.themeId} theme! Apply it in your profile.`}
            </div>
            <button style={s_GB({ width: '100%', fontSize: 15 })} onClick={() => setThemeUnlock(null)}>
              {nl ? 'Geweldig! ✨' : 'Awesome! ✨'}
            </button>
          </div>
        </div>
      )}

      {/* Coach hire request toast (for coaches) */}
      {coachHireToast && (
        <div onClick={() => { setTab(4); setCoachHireToast(null); }}
          style={{position:'fixed',bottom:newMsgToast||friendOnlineToast?206:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#1e2e18',border:'1px solid #C8A84B',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>🎓</span>
          <span style={{color:'#fff',fontSize:14,fontWeight:600}}>
            {nl ? `${coachHireToast.name} wil jou inhuren als coach` : `${coachHireToast.name} wants to hire you`}
          </span>
        </div>
      )}

      {/* Coach contract status toast (for clients) */}
      {contractStatusToast && (
        <div onClick={() => setContractStatusToast(null)}
          style={{position:'fixed',bottom:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:contractStatusToast.status==='accepted'?'#1a3a2a':'#3a1a1a',border:`1px solid ${contractStatusToast.status==='accepted'?'#4AC87A':'#C05050'}`,borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>{contractStatusToast.status==='accepted'?'✅':'❌'}</span>
          <span style={{color:'#fff',fontSize:14,fontWeight:600}}>
            {contractStatusToast.status==='accepted'
              ? (nl ? `${contractStatusToast.coachName} heeft je verzoek geaccepteerd!` : `${contractStatusToast.coachName} accepted your request!`)
              : (nl ? `${contractStatusToast.coachName} is momenteel niet beschikbaar` : `${contractStatusToast.coachName} is unavailable`)}
          </span>
        </div>
      )}

      {/* Credits received toast */}
      {creditsToast && (
        <div onClick={() => { setTab(13); setCreditsToast(null); }}
          style={{position:'fixed',bottom:friendReqToast?148:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#1e2010',border:'1px solid #C8A84B',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>⚡</span>
          <span style={{color:'#C8A84B',fontSize:14,fontWeight:600}}>{creditsToast.text}</span>
        </div>
      )}

      {/* Competition new toast */}
      {competitionToast && (
        <div onClick={() => { setTab(7); setCompetitionToast(null); }}
          style={{position:'fixed',bottom:creditsToast?148:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#1e1a10',border:'1px solid #C8A84B',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>⚔️</span>
          <span style={{color:'#fff',fontSize:14,fontWeight:600}}>{competitionToast.text}</span>
        </div>
      )}

      {/* Booking new toast (for business owners) */}
      {bookingToast && (
        <div onClick={() => { setTab(10); setBookingToast(null); }}
          style={{position:'fixed',bottom:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#101a2e',border:'1px solid #5B9ED0',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>📅</span>
          <span style={{color:'#fff',fontSize:14,fontWeight:600}}>
            {nl ? `Nieuwe boeking van ${bookingToast.name}` : `New booking from ${bookingToast.name}`}
          </span>
        </div>
      )}

      {/* Admin rights toast */}
      {adminRightsToast && (
        <div onClick={() => setAdminRightsToast(false)}
          style={{position:'fixed',bottom:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#1a1a0e',border:'1px solid #C8A84B',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>🛡️</span>
          <span style={{color:'#C8A84B',fontSize:14,fontWeight:600}}>
            {nl ? 'Je hebt adminrechten ontvangen!' : 'You have been granted admin access!'}
          </span>
        </div>
      )}

      {/* Lab rights toast */}
      {labRightsToast && (
        <div onClick={() => setLabRightsToast(false)}
          style={{position:'fixed',bottom:adminRightsToast?148:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#0e1a18',border:'1px solid #2A9E82',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>🔬</span>
          <span style={{color:'#2A9E82',fontSize:14,fontWeight:600}}>
            {nl ? 'Je hebt labtoegang ontvangen!' : 'You have been granted lab access!'}
          </span>
        </div>
      )}

      {/* Friend request toast */}
      {friendReqToast && (
        <div onClick={() => { setShowNotifs(true); setFriendReqToast(null); }}
          style={{position:'fixed',bottom:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#1e2010',border:'1px solid #C8A84B',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>🤝</span>
          <span style={{color:'#fff',fontSize:14,fontWeight:600}}>
            {nl ? `${friendReqToast.name} wil je toevoegen` : `${friendReqToast.name} sent a friend request`}
          </span>
        </div>
      )}

      {/* Group chat unread toast */}
      {groupChatToast && (
        <div onClick={() => { setTab(7); setGroupChatToast(null); }}
          style={{position:'fixed',bottom:90,left:'50%',transform:'translateX(-50%)',zIndex:8500,background:'#10201a',border:'1px solid #2A9E82',borderRadius:50,padding:'10px 20px',display:'flex',alignItems:'center',gap:10,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',cursor:'pointer',whiteSpace:'nowrap'}}>
          <span style={{fontSize:18}}>💬</span>
          <span style={{color:'#fff',fontSize:14,fontWeight:600}}>
            {groupChatToast.senderName
              ? (nl ? `${groupChatToast.senderName} in ${groupChatToast.groupName}` : `${groupChatToast.senderName} in ${groupChatToast.groupName}`)
              : (nl ? `Nieuw bericht in ${groupChatToast.groupName}` : `New message in ${groupChatToast.groupName}`)}
          </span>
        </div>
      )}

      {/* Competition popup modal */}
      {competitionPopup && (
        <div style={{position:'fixed',inset:0,zIndex:2200,display:'flex',alignItems:'center',justifyContent:'center',padding:24,background:'rgba(0,0,0,0.78)'}}>
          <div className="popIn" style={{background:_T.bg2,border:'2px solid #C8A84B',borderRadius:28,padding:28,maxWidth:340,width:'100%',textAlign:'center',position:'relative'}}>
            <button onClick={() => setCompetitionPopup(null)} style={{position:'absolute',top:12,right:12,background:'transparent',border:'none',color:C.silver,fontSize:20,cursor:'pointer',padding:4,lineHeight:1}}>✕</button>
            <div style={{fontSize:52,marginBottom:8}}>⚔️</div>
            <div style={{fontSize:11,color:'#C8A84B',letterSpacing:3,fontWeight:700,marginBottom:6}}>{nl?'NIEUWE COMPETITIE':'NEW COMPETITION'}</div>
            <div style={{fontFamily:'Georgia,serif',fontSize:18,color:C.white,marginBottom:10,lineHeight:1.4}}>{competitionPopup.text}</div>
            {competitionPopup.name && <div style={{color:C.silver,fontSize:12,marginBottom:16}}>{nl?`Gestart door ${competitionPopup.name}`:`Started by ${competitionPopup.name}`}</div>}
            <button style={{width:'100%',background:'linear-gradient(135deg,#C8A84B,#E4C97A)',border:'none',borderRadius:14,color:'#0a1220',fontSize:14,fontWeight:800,padding:'13px',cursor:'pointer',marginBottom:8}}
              onClick={() => { setTab(7); setCompetitionPopup(null); }}>
              {nl?'Bekijk competitie →':'View competition →'}
            </button>
            <button style={s_GB({width:'100%',fontSize:13})} onClick={() => setCompetitionPopup(null)}>
              {nl?'Later':'Later'}
            </button>
          </div>
        </div>
      )}

      {/* Pulse check popup (team member) */}
      {pulsePopup && !pulsePopup.answered && (
        <div style={{position:'fixed',inset:0,zIndex:2200,display:'flex',alignItems:'center',justifyContent:'center',padding:24,background:'rgba(0,0,0,0.82)'}}>
          <div className="popIn" style={{background:_T.bg2,border:`2px solid ${_T.accent}`,borderRadius:28,padding:28,maxWidth:380,width:'100%',position:'relative'}}>
            <button onClick={() => { try { const s=JSON.parse(localStorage.getItem('sl_pulse_seen')||'[]'); s.push(pulsePopup.pulse.id); localStorage.setItem('sl_pulse_seen',JSON.stringify(s)); } catch{} setPulsePopup(null); }}
              style={{position:'absolute',top:12,right:12,background:'transparent',border:'none',color:C.silver,fontSize:20,cursor:'pointer',padding:4,lineHeight:1}}>✕</button>
            <div style={{fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:700,marginBottom:8}}>{nl?'📊 TEAM PULSE CHECK':'📊 TEAM PULSE CHECK'}</div>
            <div style={{fontFamily:'Georgia,serif',fontSize:18,color:C.white,marginBottom:16,lineHeight:1.4}}>{pulsePopup.pulse.question}</div>
            {pulsePopup.pulse.options && pulsePopup.pulse.options.length > 0 ? (
              <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
                {pulsePopup.pulse.options.map(opt => (
                  <button key={opt} style={{background:_T.bg3,border:`1px solid ${_T.cardBorderBot}`,borderRadius:12,color:C.white,fontSize:14,padding:'11px 16px',cursor:'pointer',textAlign:'left'}}
                    onClick={() => {
                      fetch(`${BASE}/businesses/${encodeURIComponent(pulsePopup.businessId)}/pulse/${pulsePopup.pulse.id}/respond`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({answer:opt})}).catch(()=>{});
                      try { const s=JSON.parse(localStorage.getItem('sl_pulse_seen')||'[]'); s.push(pulsePopup.pulse.id); localStorage.setItem('sl_pulse_seen',JSON.stringify(s)); } catch{}
                      setPulsePopup(p => ({...p, answered:true}));
                    }}>
                    {opt}
                  </button>
                ))}
              </div>
            ) : (
              <div style={{marginBottom:16}}>
                <textarea id="pulse-ans" placeholder={nl?'Jouw antwoord...':'Your answer...'} rows={3}
                  style={{width:'100%',background:_T.bg3,border:`1px solid ${_T.cardBorderBot}`,borderRadius:12,color:C.white,fontSize:13,padding:'10px 12px',resize:'none',outline:'none',boxSizing:'border-box'}}/>
                <button style={{marginTop:8,width:'100%',background:`linear-gradient(135deg,${_T.grad1},${_T.grad2})`,border:'none',borderRadius:12,color:'#0a1220',fontSize:14,fontWeight:700,padding:'11px',cursor:'pointer'}}
                  onClick={() => {
                    const ans = document.getElementById('pulse-ans')?.value?.trim();
                    if (!ans) return;
                    fetch(`${BASE}/businesses/${encodeURIComponent(pulsePopup.businessId)}/pulse/${pulsePopup.pulse.id}/respond`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({answer:ans})}).catch(()=>{});
                    try { const s=JSON.parse(localStorage.getItem('sl_pulse_seen')||'[]'); s.push(pulsePopup.pulse.id); localStorage.setItem('sl_pulse_seen',JSON.stringify(s)); } catch{}
                    setPulsePopup(p => ({...p, answered:true}));
                  }}>
                  {nl?'Versturen ✓':'Submit ✓'}
                </button>
              </div>
            )}
            {pulsePopup.answered && (
              <div style={{textAlign:'center',color:C.teal,fontWeight:700,marginBottom:16}}>✓ {nl?'Antwoord verstuurd!':'Response sent!'}</div>
            )}
          </div>
        </div>
      )}

      {/* Pulse answered confirmation */}
      {pulsePopup && pulsePopup.answered && (
        <div style={{position:'fixed',inset:0,zIndex:2200,display:'flex',alignItems:'center',justifyContent:'center',padding:24,background:'rgba(0,0,0,0.82)'}}>
          <div className="popIn" style={{background:_T.bg2,border:`2px solid ${C.teal}`,borderRadius:28,padding:28,maxWidth:340,width:'100%',textAlign:'center',position:'relative'}}>
            <div style={{fontSize:52,marginBottom:8}}>✅</div>
            <div style={{fontFamily:'Georgia,serif',fontSize:18,color:C.white,marginBottom:8}}>{nl?'Bedankt voor je antwoord!':'Thanks for your response!'}</div>
            <div style={{color:C.silver,fontSize:13,marginBottom:20}}>{nl?'Je bijdrage helpt het team.':'Your input helps the team.'}</div>
            <button style={s_GB({width:'100%',fontSize:15})} onClick={() => setPulsePopup(null)}>{nl?'Sluiten':'Close'}</button>
          </div>
        </div>
      )}

      {/* Android install banner */}
      {installPrompt && (
        <div style={bannerStyle}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:26}}>📲</span>
            <div>
              <div style={{color:'#fff',fontSize:13,fontWeight:600}}>Install Sense Lifting</div>
              <div style={{color:'#6b7a99',fontSize:11}}>Add to your home screen for the best experience</div>
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
            <button onClick={androidInstall} style={{background:'#C8A84B',border:'none',borderRadius:20,color:'#000',fontSize:12,fontWeight:700,padding:'7px 14px',cursor:'pointer'}}>Install</button>
            <button onClick={()=>setInstallPrompt(false)} style={dismissBtn}>✕</button>
          </div>
        </div>
      )}

      {/* iOS install banner */}
      {showIosBanner && (
        <div style={bannerStyle}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:26}}>📲</span>
            <div>
              <div style={{color:'#fff',fontSize:13,fontWeight:600}}>{nl ? 'App installeren' : 'Install Sense Lifting'}</div>
              <div style={{color:'#6b7a99',fontSize:11}}>{nl ? 'Tik op' : 'Tap'} <strong style={{color:'#C8A84B'}}>Delen ↑</strong> {nl ? 'dan' : 'then'} <strong style={{color:'#C8A84B'}}>{nl ? 'Zet op beginscherm' : 'Add to Home Screen'}</strong></div>
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
            <button onClick={()=>{setShowIosBanner(false);setShowIosInstall(true);}} style={{background:'#C8A84B',border:'none',borderRadius:20,color:'#000',fontSize:12,fontWeight:700,padding:'7px 14px',cursor:'pointer'}}>{nl ? 'Hoe?' : 'How?'}</button>
            <button onClick={dismissIos} style={dismissBtn}>✕</button>
          </div>
        </div>
      )}

      {/* iOS install step-by-step modal */}
      {showIosInstall && (
        <div style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'flex-end',justifyContent:'center'}} onClick={()=>setShowIosInstall(false)}>
          <div style={{background:'#1a2235',borderRadius:'20px 20px 0 0',padding:'28px 24px 40px',width:'100%',maxWidth:480,boxShadow:'0 -8px 40px rgba(0,0,0,0.5)'}} onClick={e=>e.stopPropagation()}>
            <div style={{textAlign:'center',marginBottom:20}}>
              <div style={{fontSize:36,marginBottom:8}}>📲</div>
              <div style={{fontFamily:'Georgia,serif',fontSize:18,color:'#EEF3FF',marginBottom:6}}>{nl ? 'Installeer de app' : 'Install the App'}</div>
              <div style={{fontSize:13,color:'#6b7a99'}}>{nl ? 'Volg deze stappen in Safari:' : 'Follow these steps in Safari:'}</div>
            </div>
            {[
              { icon:'1️⃣', text: nl ? 'Tik op het Deel-icoon (□↑) onderaan Safari' : 'Tap the Share icon (□↑) at the bottom of Safari' },
              { icon:'2️⃣', text: nl ? 'Scroll naar beneden en tik op "Zet op beginscherm"' : 'Scroll down and tap "Add to Home Screen"' },
              { icon:'3️⃣', text: nl ? 'Tik op "Voeg toe" rechtsboven' : 'Tap "Add" in the top right' },
            ].map((step,i) => (
              <div key={i} style={{display:'flex',alignItems:'flex-start',gap:12,marginBottom:16,background:'rgba(255,255,255,0.04)',borderRadius:12,padding:'12px 14px'}}>
                <span style={{fontSize:20,flexShrink:0}}>{step.icon}</span>
                <span style={{fontSize:13,color:'#c9d4e8',lineHeight:1.5}}>{step.text}</span>
              </div>
            ))}
            <div style={{fontSize:11,color:'#4a5568',textAlign:'center',marginBottom:20}}>{nl ? '⚠️ Werkt alleen in Safari op iPhone/iPad' : '⚠️ Only works in Safari on iPhone/iPad'}</div>
            <button onClick={()=>setShowIosInstall(false)} style={{width:'100%',background:'#C8A84B',border:'none',borderRadius:14,color:'#000',fontSize:15,fontWeight:700,padding:'14px',cursor:'pointer'}}>{nl ? 'Begrepen' : 'Got it'}</button>
          </div>
        </div>
      )}
      {showLangPicker && <LanguagePicker onClose={()=>setShowLangPicker(false)} lang={lang} onSelect={code=>{setLang(code);document.documentElement.dir=isRTL(code)?"rtl":"ltr";}}/>}
      {connStatus === 'offline' && (
        <div style={{position:'fixed',inset:0,zIndex:99999,background:'#040810',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:20,padding:32,fontFamily:'Inter,system-ui,sans-serif'}}>
          <div style={{fontSize:56,lineHeight:1}}>📡</div>
          <h2 style={{color:'#fff',margin:0,fontSize:22,fontWeight:700,textAlign:'center'}}>{lang==='nl'?'Geen verbinding':'No connection'}</h2>
          <p style={{color:'rgba(255,255,255,0.5)',margin:0,fontSize:15,textAlign:'center',maxWidth:280}}>{lang==='nl'?'Controleer je internet­verbinding en probeer opnieuw.':'Check your internet connection and try again.'}</p>
          <button onClick={()=>{connFailRef.current=0;setConnStatus('loading');}}
            style={{marginTop:8,padding:'12px 32px',borderRadius:14,background:'linear-gradient(135deg,#C8A84B,#e2c97e)',border:'none',color:'#0a1220',fontSize:15,fontWeight:700,cursor:'pointer'}}>
            {lang==='nl'?'Opnieuw proberen':'Try again'}
          </button>
          <p style={{color:'rgba(255,255,255,0.2)',margin:0,fontSize:11}}>{lang==='nl'?'Verbinding controleren…':'Checking connection…'}</p>
        </div>
      )}
      <div data-theme={themeId} className="sl-app-root" style={{background:globalBg,minHeight:"100vh",fontFamily:"Inter,system-ui,sans-serif",color:C.white,display:"flex",flexDirection:"column"}}>
        <style>{CSS}</style>

        {showNotifs && (
          <NotificationsDrawer
            notifications={notifications} setNotifications={setNotifications}
            lang={lang} onClose={()=>setShowNotifs(false)}
            onNav={t=>{setTab(t);}} friends={friends}
            onAcceptFriend={acceptFriend} onDeclineFriend={declineFriend}
            currentUserId={currentUserId}
            onGameInvite={meta=>{setPendingGameInvite({token:meta.token,gameTitle:meta.gameTitle,inviterName:meta.inviterName,mode:meta.mode});setTab(3);setShowNotifs(false);}}
            onChatNotif={()=>{setTab(7);setCommunityInitTab('chat');setShowNotifs(false);setTimeout(()=>setCommunityInitTab(null),500);}}
          />
        )}
        {showTips      && <TipsModal       onClose={()=>setShowTips(false)}      lang={lang}/>}
        {showReminders && <RemindersSheet  onClose={()=>setShowReminders(false)} lang={lang}/>}
        {showScreenShare && <ScreenShareModal onClose={()=>setShowScreenShare(false)} lang={lang}/>}
        {showLicense   && <LicenseSheet   onClose={()=>setShowLicense(false)}   lang={lang} license={license} onLicenseChange={setLicense} signedUpAt={signedUpAt}/>}
        {onboardDone && <GroupInvitePopup notifications={notifications} currentUserId={currentUserId} userName={userName} lang={lang} setNotifications={setNotifications}/>}
        {signedUpAt && license==="freemium" && !sportFlowPhase && (
          <OnboardingCoach
            lang={lang}
            signedUpAt={signedUpAt}
            onAddNotification={addNotification}
            onOpenLicense={()=>setShowLicense(true)}
            onNav={setTab}
          />
        )}
        {/* Freemium lockout after 7 days — full-screen TrialEndModal */}
        {signedUpAt && license==="freemium" && !businessId && (() => {
          const days = Math.floor((Date.now() - new Date(signedUpAt).getTime()) / 86400000);
          if (days < 7) return null;
          const daysLeft = Math.max(0, 10 - days);
          return (
            <TrialEndModal
              lang={lang}
              variant="urgent"
              daysLeftUntilDeletion={daysLeft}
              onLicenseChange={setLicense}
            />
          );
        })()}

        {/* Day-7 last-day popup (closeable) */}
        {showTrialEnd && trialEndVariant === 'last_day' && (
          <TrialEndModal
            lang={lang}
            variant="last_day"
            onClose={() => setShowTrialEnd(false)}
            onLicenseChange={setLicense}
          />
        )}

        {/* ── Desktop / Tablet sidebar ── */}
        <aside className="sl-sidebar" style={{background:_T.bg2,borderRight:`1px solid ${_T.cardBorderTop}`}}>
          {/* Logo */}
          <div style={{padding:'20px 12px 16px',borderBottom:`1px solid ${_T.cardBorderTop}`,display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
            <img src="/butterfly.png" alt="SL" style={{height:32,width:'auto',mixBlendMode:'screen',filter:`drop-shadow(0 0 6px ${_T.accent}90)`,opacity:0.92,flexShrink:0}}/>
            <span className="sl-sidebar-label" style={{color:_T.accent,fontSize:15,fontWeight:700,letterSpacing:0.3,fontFamily:'Georgia,serif',whiteSpace:'nowrap'}}>Sense Lifting</span>
          </div>

          {/* Nav items */}
          <nav style={{flex:1,padding:'10px 8px',overflowY:'auto',overflowX:'hidden'}}>
            {[
              {icon:'🏠', label:nl?'Dashboard':'Dashboard', t:0},
              {icon:'📅', label:nl?'Agenda':'Calendar',     t:1},
              {icon:'🧘', label:nl?'Oefeningen':'Exercises', t:2},
              {icon:'🏋️', label:nl?'Training':'Training',   t:3},
              {icon:'🦋', label:'Coach',                     t:4},
              {icon:'🎯', label:nl?'Doelen':'Goals',         t:5},
              {icon:'👥', label:'Community',                  t:7},
              {icon:'😴', label:nl?'Slaap & Stemming':'Sleep & Mood', t:8},
              ...(hasWallet    ? [{icon:'💰', label:'Wallet',                   t:13}] : []),
              ...(businessId   ? [{icon:'🏢', label:nl?'Bedrijf':'Business',    t:10}] : []),
              ...(isResearcher ? [{icon:'🔬', label:'Lab',                       t:12}] : []),
              ...(isAdmin      ? [{icon:'🛡️', label:'Office',                   t:11}] : []),
            ].map(n => {
              const chatUnread = n.t===7 ? Object.values(conversations).reduce((s,msgs)=>s+(Array.isArray(msgs)?msgs.filter(m=>m.from!==currentUserId&&!m.read).length:0),0) : 0;
              const badge = n.t===7 ? (incoming.length + chatUnread) : 0;
              const active = tab === n.t;
              return (
                <button key={n.t} onClick={()=>{playSound('nav');n.t===10?navToBiz(10):setTab(n.t);}} title={n.label}
                  style={{display:'flex',alignItems:'center',gap:12,width:'100%',
                    background:active?`${_T.accent}15`:litNavTabs.has(n.t)?'rgba(200,168,75,0.07)':'transparent',
                    border:`1px solid ${active?_T.accent+'33':litNavTabs.has(n.t)?'rgba(200,168,75,0.5)':'transparent'}`,
                    borderRadius:12,padding:'10px 10px',cursor:'pointer',
                    color:active?_T.accent:litNavTabs.has(n.t)?'#C8A84B':C.silver,
                    fontSize:14,fontWeight:active?600:litNavTabs.has(n.t)?600:400,marginBottom:3,textAlign:'left',position:'relative',fontFamily:'Inter,system-ui,sans-serif',
                    transition:'background 0.3s,color 0.3s,border-color 0.3s,box-shadow 0.3s',
                    boxShadow:litNavTabs.has(n.t)?'0 0 14px rgba(200,168,75,0.25), inset 0 0 8px rgba(200,168,75,0.05)':'none',
                    boxSizing:'border-box'}}>
                  <span style={{fontSize:20,flexShrink:0,lineHeight:1}}>{n.icon}</span>
                  <span className="sl-sidebar-label" style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{n.label}</span>
                  {badge>0 && <div style={{position:'absolute',top:8,left:30,minWidth:15,height:15,borderRadius:8,background:'#FF3B30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,color:'#fff',padding:'0 2px'}}>{badge>99?'99+':badge}</div>}
                  {active && <div className="sl-sidebar-label" style={{width:3,height:14,borderRadius:2,background:_T.accent,flexShrink:0}}/>}
                </button>
              );
            })}
          </nav>

          {/* Bottom: user + wallet + actions */}
          <div style={{padding:'10px 8px',borderTop:`1px solid ${_T.cardBorderTop}`,flexShrink:0}}>
            {/* User row */}
            <button onClick={()=>{playSound('nav');setTab(0);}} title={userName||'Profile'}
              style={{display:'flex',alignItems:'center',gap:10,width:'100%',background:'transparent',border:'none',borderRadius:12,padding:'8px 8px',cursor:'pointer',marginBottom:6,boxSizing:'border-box'}}>
              <div style={{width:34,height:34,borderRadius:'50%',overflow:'hidden',flexShrink:0,border:`2px solid ${_T.accent}44`}}>
                {(localStorage.getItem(`sl_avatar_${currentUserId}`) || googlePicture)
                  ? <img src={localStorage.getItem(`sl_avatar_${currentUserId}`) || googlePicture} alt="" style={{width:34,height:34,objectFit:'cover'}}/>
                  : <div style={{width:34,height:34,background:`${_T.accent}20`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,color:_T.accent}}>👤</div>}
              </div>
              <div className="sl-sidebar-label" style={{flex:1,minWidth:0,textAlign:'left'}}>
                <div style={{color:C.white,fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{userName||'Profile'}</div>
                <div style={{color:_T.accent,fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:1}}>{license||'freemium'}</div>
              </div>
            </button>
            {/* Wallet — show business credits for members, personal otherwise */}
            {(businessId ? bizWalletBalance : walletBalance) > 0 && (
              <button onClick={()=>{playSound('click');setTab(13);}} title={`${businessId ? bizWalletBalance : walletBalance} credits`}
                style={{display:'flex',alignItems:'center',gap:8,width:'100%',background:'rgba(200,168,75,0.08)',border:'1px solid rgba(200,168,75,0.2)',borderRadius:10,padding:'6px 10px',cursor:'pointer',marginBottom:6,boxSizing:'border-box'}}>
                <span style={{fontSize:16,flexShrink:0}}>{businessId ? '🏢' : '⚡'}</span>
                <span className="sl-sidebar-label" style={{color:'#C8A84B',fontSize:12,fontWeight:700,whiteSpace:'nowrap'}}>{businessId ? bizWalletBalance : walletBalance} credits</span>
              </button>
            )}
            {/* Notification + Settings */}
            <div style={{display:'flex',gap:6}}>
              <button onClick={()=>{playSound('notif');setShowNotifs(true);}} title={nl?'Notificaties':'Notifications'}
                style={{flex:1,height:36,borderRadius:10,background:unreadCount>0?`${_T.accent}15`:_T.bg4,border:`1px solid ${unreadCount>0?_T.accent:_T.cardBorderBot}`,cursor:'pointer',fontSize:17,display:'flex',alignItems:'center',justifyContent:'center',position:'relative'}}>
                🔔
                {unreadCount>0&&<div style={{position:'absolute',top:-4,right:-4,minWidth:15,height:15,borderRadius:8,background:C.red,display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:700,color:'white',padding:'0 2px'}}>{unreadCount}</div>}
              </button>
              <div style={{position:'relative',flex:1}}>
                <button onClick={()=>{playSound('filter');setShowSettingsMenu(v=>!v);}} title={nl?'Instellingen':'Settings'}
                  style={{width:'100%',height:36,borderRadius:10,background:showSettingsMenu?`${_T.accent}25`:_T.bg4,border:`1px solid ${showSettingsMenu?_T.accent:_T.cardBorderBot}`,cursor:'pointer',fontSize:17,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  ⚙️
                </button>
              </div>
            </div>
            {/* Settings dropdown (desktop only — positioned via CSS) */}
            {showSettingsMenu && (
              <>
                <div onClick={()=>setShowSettingsMenu(false)} style={{position:'fixed',inset:0,zIndex:9998}}/>
                <div className="sl-sidebar-settings-menu">
                  {[
                    {icon:'🎓', label:nl?'App rondleiding':'App tour',        action:()=>{setShowSettingsMenu(false);setShowTour(true);}},
                    {icon:'🔔', label:nl?'Herinneringen':'Reminders',          action:()=>{setShowSettingsMenu(false);setShowReminders(true);}},
                    {icon:'🪪', label:nl?'Licentie':'License',                 action:()=>{setShowSettingsMenu(false);setShowLicense(true);}},
                    {icon:'🌐', label:`${langMeta.flag} ${nl?'Taal':'Language'}`, action:()=>{setShowSettingsMenu(false);setShowLangPicker(true);}},
                    {icon:'🔒', label:nl?'Privacy':'Privacy',                  action:()=>{setShowSettingsMenu(false);window.open('/privacy','_blank');}},
                    {icon:'📋', label:nl?'Voorwaarden':'Terms',                action:()=>{setShowSettingsMenu(false);window.open('/terms','_blank');}},
                    {icon:'🚪', label:nl?'Uitloggen':'Log out',                action:()=>{setShowSettingsMenu(false);Object.keys(localStorage).filter(k=>k.startsWith('sl_')).forEach(k=>localStorage.removeItem(k));window.location.reload();}, danger:true},
                  ].map(item=>(
                    <button key={item.label} onClick={()=>{playSound(item.danger?'notif':'tab');item.action();}} style={{display:'flex',alignItems:'center',gap:10,width:'100%',background:'none',border:'none',padding:'11px 16px',cursor:'pointer',fontSize:13,color:item.danger?C.red:C.white,textAlign:'left',fontFamily:'Inter,system-ui,sans-serif'}}>
                      <span style={{fontSize:16,minWidth:20}}>{item.icon}</span>
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </aside>

        {/* ── Main content (mobile: full width; tablet/desktop: flex:1 next to sidebar) ── */}
        <div className="sl-main" style={{flex:1,display:'flex',flexDirection:'column',minWidth:0}}>

        {/* ── Top nav ── */}
        <div data-nav className="sl-top-nav" style={{background:`${_T.bg2}ee`,borderBottom:`1px solid ${_T.cardBorderTop}`,paddingTop:'calc(10px + env(safe-area-inset-top))',paddingBottom:'10px',paddingLeft:'16px',paddingRight:'16px',display:(tab===10||tab===11||tab===12)?"none":"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50,backdropFilter:"blur(16px)"}}>
          <div style={{display:"flex",gap:8,position:"relative"}}>
            {/* Profile photo button — single click = dropdown, double click = toggle dashboard */}
            {(()=>{
              const hasCoach = !!(license && license.startsWith('coaches'));
              const hasBiz = !!(businessId || businesses.length > 0);
              const bizLogo = (bizDisplayLogo || '').trim() || null;
              const bizName = bizDisplayName;
              const profileSrc = localStorage.getItem(`sl_avatar_${currentUserId}`) || googlePicture;
              const handleProfileClick = () => {
                playSound('click');
                if (profileClickTimer.current) {
                  clearTimeout(profileClickTimer.current);
                  profileClickTimer.current = null;
                  setShowProfileDropdown(false);
                  if (hasBiz) navToBiz(tab === 10 ? 0 : 10);
                  else setTab(0);
                } else {
                  profileClickTimer.current = setTimeout(() => {
                    profileClickTimer.current = null;
                    // Refresh businesses on every dropdown open so login race condition is covered
                    const uid = localStorage.getItem('sl_user_id');
                    console.log('[profile-dropdown] uid=', uid, 'businessId=', businessId, 'businesses=', businesses.map(b=>b.id));
                    if (uid) loadMyBusinesses(uid, businessId || null);
                    // Show dropdown if has business, coach license, or is admin
                    if (hasBiz || hasCoach || isAdmin || isResearcher) {
                      setShowProfileDropdown(s => !s);
                    } else {
                      setTab(0);
                    }
                  }, 220);
                }
              };
              return (
                <>
                  <button onClick={handleProfileClick}
                    style={{position:"relative",width:42,height:42,borderRadius:"50%",background:(tab===0||tab===10)?`${_T.accent}25`:_T.bg4,border:`1px solid ${(tab===0||tab===10)?_T.accent:_T.cardBorderBot}`,cursor:"pointer",fontSize:22,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",padding:0,flexShrink:0}}>
                    {profileSrc
                      ? <img src={profileSrc} alt="" style={{width:42,height:42,objectFit:"cover",borderRadius:"50%"}}/>
                      : "👤"}
                    {/* Business indicator dot */}
                    {hasBiz && tab===10 && <div style={{position:"absolute",bottom:1,right:1,width:10,height:10,borderRadius:"50%",background:_T.accent,border:`2px solid ${_T.bg2}`}}/>}
                  </button>

                  {/* Dropdown */}
                  {showProfileDropdown && (
                    <>
                      <div onClick={()=>setShowProfileDropdown(false)} style={{position:"fixed",inset:0,zIndex:98}}/>
                      <div style={{position:"absolute",top:50,left:0,zIndex:99,background:_T.bg2,border:`1px solid ${_T.cardBorderTop}`,borderRadius:16,padding:6,minWidth:200,boxShadow:"0 8px 32px rgba(0,0,0,0.5)",backdropFilter:"blur(16px)"}}>
                        {/* Personal */}
                        <button onClick={()=>{playSound('nav');setTab(0);setShowProfileDropdown(false);}}
                          style={{width:"100%",background:tab===0?`${_T.accent}15`:"transparent",border:"none",borderRadius:12,padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,textAlign:"left"}}>
                          <div style={{width:32,height:32,borderRadius:"50%",overflow:"hidden",flexShrink:0,border:`2px solid ${tab===0?_T.accent:_T.cardBorderBot}`}}>
                            {profileSrc
                              ? <img src={profileSrc} alt="" style={{width:32,height:32,objectFit:"cover"}}/>
                              : <div style={{width:32,height:32,background:`${_T.accent}20`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:_T.accent}}>👤</div>}
                          </div>
                          <div>
                            <div style={{color:tab===0?_T.accent:C.white,fontSize:13,fontWeight:700}}>{lang==='nl'?'Persoonlijk':'Personal'}</div>
                            <div style={{color:C.silver,fontSize:11}}>{lang==='nl'?'Mijn dashboard':'My dashboard'}</div>
                          </div>
                          {tab===0 && <div style={{marginLeft:"auto",width:6,height:6,borderRadius:"50%",background:_T.accent,flexShrink:0}}/>}
                        </button>

                        {/* Coach Dashboard */}
                        {hasCoach && (
                          <>
                            <div style={{height:1,background:_T.cardBorderBot,margin:"4px 8px"}}/>
                            <button onClick={()=>{playSound('nav');setTab(4);setShowProfileDropdown(false);}}
                              style={{width:"100%",background:tab===4?`${_T.accent}15`:"transparent",border:"none",borderRadius:12,padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,textAlign:"left"}}>
                              <div style={{width:32,height:32,borderRadius:8,background:`${_T.accent}20`,border:`2px solid ${tab===4?_T.accent:_T.cardBorderBot}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                <span style={{fontSize:18}}>🎯</span>
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{color:tab===4?_T.accent:C.white,fontSize:13,fontWeight:700}}>
                                  {lang==='nl'?'Coach Dashboard':'Coach Dashboard'}
                                </div>
                                <div style={{color:C.silver,fontSize:11}}>
                                  {(()=>{const t=license?.split('_')[1];return t?t.charAt(0).toUpperCase()+t.slice(1)+' Package':'Coach';})()}
                                </div>
                              </div>
                              {tab===4 && <div style={{marginLeft:"auto",width:6,height:6,borderRadius:"50%",background:_T.accent,flexShrink:0}}/>}
                            </button>
                          </>
                        )}

                        {/* Business(es) */}
                        {hasBiz && (
                          <>
                            <div style={{height:1,background:_T.cardBorderBot,margin:"4px 8px"}}/>
                            <div style={{padding:"4px 8px 2px",fontSize:9,color:C.silver2,letterSpacing:2,fontWeight:700}}>{lang==='nl'?'BEDRIJVEN':'BUSINESSES'}</div>
                            {(businesses.length > 0 ? businesses : [{id: businessId, name: bizName||'Business', logo_url: bizLogo, role:'member'}]).map(biz => {
                              const isActive = biz.id === businessId;
                              const logoSrc = (biz.logo_url || '').trim() || null;
                              return (
                                <button key={biz.id} onClick={()=>{ playSound('nav'); switchBusiness(biz); setTab(10); setShowProfileDropdown(false); }}
                                  style={{width:"100%",background:(tab===10&&isActive)?`${_T.accent}15`:"transparent",border:"none",borderRadius:12,padding:"8px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,textAlign:"left"}}>
                                  <div style={{width:30,height:30,borderRadius:7,overflow:"hidden",flexShrink:0,border:`2px solid ${(tab===10&&isActive)?_T.accent:_T.cardBorderBot}`,background:_T.bg4,display:"flex",alignItems:"center",justifyContent:"center"}}>
                                    {logoSrc
                                      ? <img src={logoSrc} alt="" style={{width:30,height:30,objectFit:"cover"}} onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>
                                      : null}
                                    <span style={{fontSize:15,display:logoSrc?'none':'flex'}}>🏢</span>
                                  </div>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{color:(tab===10&&isActive)?_T.accent:C.white,fontSize:12,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{biz.name||'Business'}</div>
                                    <div style={{color:C.silver2,fontSize:10}}>{biz.role==='owner'?(lang==='nl'?'Eigenaar':'Owner'):(lang==='nl'?'Lid':'Member')}</div>
                                  </div>
                                  {tab===10&&isActive && <div style={{width:6,height:6,borderRadius:"50%",background:_T.accent,flexShrink:0}}/>}
                                </button>
                              );
                            })}
                          </>
                        )}

                        {/* Admin dashboard */}
                        {isAdmin && (
                          <>
                            <div style={{height:1,background:_T.cardBorderBot,margin:"4px 8px"}}/>
                            <button onClick={()=>{playSound('notif');setTab(11);setShowProfileDropdown(false);}}
                              style={{width:"100%",background:tab===11?`rgba(255,80,80,0.12)`:"transparent",border:"none",borderRadius:12,padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,textAlign:"left"}}>
                              <div style={{width:32,height:32,borderRadius:8,background:"rgba(255,80,80,0.15)",border:`2px solid ${tab===11?"#FF5050":_T.cardBorderBot}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                <span style={{fontSize:16}}>🛡️</span>
                              </div>
                              <div>
                                <div style={{color:tab===11?"#FF8080":C.white,fontSize:13,fontWeight:700}}>Admin</div>
                                <div style={{color:C.silver,fontSize:11}}>{lang==='nl'?'Platform beheer':'Platform management'}</div>
                              </div>
                              {tab===11 && <div style={{marginLeft:"auto",width:6,height:6,borderRadius:"50%",background:"#FF5050",flexShrink:0}}/>}
                            </button>
                          </>
                        )}
                        {isResearcher && (
                          <>
                            <div style={{height:1,background:_T.cardBorderBot,margin:"4px 8px"}}/>
                            <button onClick={()=>{setTab(12);setShowProfileDropdown(false);}}
                              style={{width:"100%",background:tab===12?`rgba(155,109,176,0.12)`:"transparent",border:"none",borderRadius:12,padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,textAlign:"left"}}>
                              <div style={{width:32,height:32,borderRadius:8,background:"rgba(155,109,176,0.15)",border:`2px solid ${tab===12?"#9B6DB0":_T.cardBorderBot}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                <span style={{fontSize:16}}>🔬</span>
                              </div>
                              <div>
                                <div style={{color:tab===12?"#C09BD0":C.white,fontSize:13,fontWeight:700}}>Lab</div>
                                <div style={{color:C.silver,fontSize:11}}>{lang==='nl'?'Researcher omgeving':'Researcher environment'}</div>
                              </div>
                              {tab===12 && <div style={{marginLeft:"auto",width:6,height:6,borderRadius:"50%",background:"#9B6DB0",flexShrink:0}}/>}
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </>
              );
            })()}

            {/* Calendar + Community buttons */}
            {[{icon:"📅",t:1},{icon:"🌍",t:7}].map(({icon,t})=>{
              const chatUnread = t===7 ? Object.values(conversations).reduce((sum, msgs) =>
                sum + (Array.isArray(msgs) ? msgs.filter(m => m.from !== currentUserId && !m.read).length : 0), 0) : 0;
              const total = t===7 ? (incoming.length + chatUnread) : 0;
              return (
                <div key={t} style={{position:"relative",flexShrink:0}}>
                  <button onClick={()=>{playSound('nav');setTab(t);}} style={{width:42,height:42,borderRadius:"50%",background:tab===t?`${_T.accent}25`:_T.bg4,border:`1px solid ${tab===t?_T.accent:_T.cardBorderBot}`,cursor:"pointer",fontSize:22,display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                    <span>{icon}</span>
                  </button>
                  {total > 0 && (
                    <div style={{position:"absolute",top:-2,right:-2,minWidth:16,height:16,borderRadius:8,background:"#FF3B30",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"#fff",padding:"0 3px",pointerEvents:"none",lineHeight:1}}>
                      {total > 99 ? "99+" : total}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Centre logo ── */}
          <button onClick={()=>{playSound('tab');setTab(0);}} style={{background:"none",border:"none",cursor:"pointer",padding:0,position:"absolute",left:"50%",transform:"translateX(-50%)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <img
              src="/butterfly.png"
              alt="Sense Lifting"
              style={{
                height:46,
                width:"auto",
                mixBlendMode:"screen",
                filter:`drop-shadow(0 0 8px ${_T.accent}90) drop-shadow(0 0 2px ${_T.accent}60)`,
                opacity: 0.92,
              }}
            />
          </button>

          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button onClick={()=>{playSound('click');setShowTips(true);}} style={{width:40,height:40,borderRadius:"50%",background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,cursor:"pointer",fontSize:20,display:"flex",alignItems:"center",justifyContent:"center"}}>💡</button>
            <button onClick={()=>{
              playSound('notif');
              setShowNotifs(true);
              if (!isNative() && 'Notification' in window && Notification.permission === 'default') {
                Notification.requestPermission().then(p => { if (p === 'granted') subscribeToPush(); });
              }
            }} style={{position:"relative",width:40,height:40,borderRadius:"50%",background:unreadCount>0?`${_T.accent}20`:_T.bg4,border:`1px solid ${unreadCount>0?_T.accent:_T.cardBorderBot}`,cursor:"pointer",fontSize:20,display:"flex",alignItems:"center",justifyContent:"center"}}>
              🔔
              {unreadCount>0 && <div style={{position:"absolute",top:-3,right:-3,minWidth:16,height:16,borderRadius:8,background:C.red,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"white",padding:"0 3px"}}>{unreadCount}</div>}
            </button>
            {/* ── Cogs / settings button ── */}
            <div style={{position:"relative"}}>
              <button onClick={()=>{playSound('filter');setShowSettingsMenu(v=>!v);}} style={{width:40,height:40,borderRadius:"50%",background:showSettingsMenu?`${_T.accent}25`:_T.bg4,border:`1px solid ${showSettingsMenu?_T.accent:_T.cardBorderBot}`,cursor:"pointer",fontSize:19,display:"flex",alignItems:"center",justifyContent:"center"}}>⚙️</button>
              {showSettingsMenu&&(
                <>
                  {/* Click-outside overlay */}
                  <div style={{position:"fixed",inset:0,zIndex:98}} onClick={()=>setShowSettingsMenu(false)}/>
                  {/* Dropdown */}
                  <div style={{position:"absolute",top:46,right:0,zIndex:99,background:_T.bg2,border:`1px solid ${_T.cardBorderTop}`,borderRadius:16,padding:"6px 0",minWidth:180,boxShadow:"0 8px 32px rgba(0,0,0,0.5)"}}>
                    {[
                      {icon:"🎓", label:nl?'App rondleiding':'App tour', action:()=>{setShowSettingsMenu(false);setShowTour(true);}},
                      {icon:"🔔", label:t("settings.reminders",lang), action:()=>{setShowSettingsMenu(false);setShowReminders(true);}},
                      {icon:"🪪", label:t("settings.license",lang),   action:()=>{setShowSettingsMenu(false);setShowLicense(true);}},
                      {icon:"🌐", label:`${langMeta.flag} ${t("settings.language",lang)}`, action:()=>{setShowSettingsMenu(false);setShowLangPicker(true);}},
                      ...(!window.matchMedia('(display-mode: standalone)').matches && !window.navigator.standalone ? [{
                        icon:"📲",
                        label: nl ? 'App installeren' : 'Install App',
                        action: () => {
                          setShowSettingsMenu(false);
                          if (deferredPrompt.current) { androidInstall(); }
                          else { setShowIosInstall(true); }
                        }
                      }] : []),
                      {icon:"📺", label:nl?'Scherm delen':'Share Screen', action:()=>{setShowSettingsMenu(false);setShowScreenShare(true);}},
                      {icon:"🔒", label:t("settings.privacy",lang),   action:()=>{setShowSettingsMenu(false);window.open('/privacy','_blank');}},
                      {icon:"📋", label:t("settings.terms",lang),     action:()=>{setShowSettingsMenu(false);window.open('/terms','_blank');}},
                      {icon:"🚪", label:t("settings.logout",lang),    action:()=>{setShowSettingsMenu(false);Object.keys(localStorage).filter(k=>k.startsWith('sl_')).forEach(k=>localStorage.removeItem(k));window.location.reload();}, danger:true},
                    ].map(item=>(
                      <button key={item.label} onClick={()=>{playSound(item.danger?'notif':'tab');item.action();}} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"none",border:"none",padding:"11px 16px",cursor:"pointer",fontSize:13,color:item.danger?C.red:C.white,textAlign:"left",fontFamily:"Inter,system-ui,sans-serif"}}>
                        <span style={{fontSize:16,minWidth:20}}>{item.icon}</span>
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Desktop header bar (hidden on mobile via CSS) ── */}
        {(tab!==10 && tab!==11 && tab!==12) && (() => {
          const TAB_META = {
            0:  {icon:'🏠', name: nl?'Dashboard':'Dashboard'},
            1:  {icon:'📅', name: nl?'Agenda':'Calendar'},
            2:  {icon:'🧘', name: nl?'Oefeningen':'Exercises'},
            3:  {icon:'🏋️', name: nl?'Training':'Training'},
            4:  {icon:'🦋', name: 'Coach'},
            5:  {icon:'🎯', name: nl?'Doelen':'Goals'},
            7:  {icon:'👥', name: 'Community'},
            8:  {icon:'😴', name: nl?'Slaap & Stemming':'Sleep & Mood'},
            13: {icon:'💰', name: 'Wallet'},
          };
          const meta = TAB_META[tab] || {icon:'🏠', name:'Dashboard'};
          return (
            <div className="sl-desktop-header">
              <div className="sl-desktop-header-title">
                <span style={{fontSize:22,lineHeight:1,flexShrink:0}}>{meta.icon}</span>
                <h2 style={{color:'#fff',fontSize:19,fontWeight:700,margin:0,letterSpacing:-0.3}}>{meta.name}</h2>
              </div>
              <div className="sl-topbar-search">
                <span style={{color:'rgba(255,255,255,0.28)',fontSize:14,flexShrink:0}}>🔍</span>
                <input placeholder={nl?'Zoeken…':'Search…'} onFocus={e=>e.target.select()} />
              </div>
              <div className="sl-desktop-header-actions">
                <button onClick={()=>{playSound('notif');setShowNotifs(true);}} title={nl?'Notificaties':'Notifications'}
                  style={{width:38,height:38,borderRadius:10,background:unreadCount>0?`${_T.accent}18`:'rgba(255,255,255,0.05)',border:`1px solid ${unreadCount>0?_T.accent+'44':'rgba(255,255,255,0.08)'}`,cursor:'pointer',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center',position:'relative',flexShrink:0}}>
                  🔔
                  {unreadCount>0&&<div style={{position:'absolute',top:-3,right:-3,minWidth:14,height:14,borderRadius:7,background:'#FF3B30',display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:700,color:'white',padding:'0 2px'}}>{unreadCount>99?'99+':unreadCount}</div>}
                </button>
                <div style={{width:1,height:24,background:'rgba(255,255,255,0.07)',flexShrink:0}}/>
                <button onClick={()=>{playSound('nav');setTab(0);}} title={userName||'Profile'}
                  style={{width:36,height:36,borderRadius:'50%',overflow:'hidden',border:`2px solid ${tab===0?_T.accent:_T.cardBorderBot}`,cursor:'pointer',background:`${_T.accent}22`,padding:0,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  {(localStorage.getItem(`sl_avatar_${currentUserId}`) || googlePicture)
                    ? <img src={localStorage.getItem(`sl_avatar_${currentUserId}`) || googlePicture} alt="" style={{width:36,height:36,objectFit:'cover'}}/>
                    : <span style={{fontSize:16}}>👤</span>}
                </button>
              </div>
            </div>
          );
        })()}

        {/* ── Screen content ── */}
        <div id="app-scroll" ref={scrollRef}
          style={{
            flex:1, padding:(tab===10||tab===11||tab===12)?0:16, minHeight:0,
            overflowY: (tab===10||tab===11||tab===12) ? 'auto' : 'auto',
            overflow:  tab===10 ? 'hidden' : undefined,
            opacity:   (bizTransition?.phase==='out' || bizTransition?.phase==='flash') ? 0 : 1,
            transform: bizTransition?.phase==='out' ? 'scale(1.04) translateY(-6px)' : bizTransition?.phase==='in' ? 'scale(1) translateY(0)' : undefined,
            transition: bizTransition?.phase==='out' ? 'opacity 0.18s ease-in, transform 0.18s ease-in'
                      : bizTransition?.phase==='in'  ? 'opacity 0.42s ease-out, transform 0.42s cubic-bezier(0.34,1.56,0.64,1)'
                      : 'none',
          }}>
          <div key={tab} style={{height:(tab===10||tab===11||tab===12)?"100%":undefined}} className={THEME_TAB_CLASS[themeId] || 'tab-anim-gold'}>
            {screenMap[tab] || screenMap[0]}
          </div>
        </div>

        {/* ── Bottom nav ── */}
        <div data-nav className="sl-bottom-nav" style={{background:`${_T.bg2}ee`,borderTop:`1px solid ${_T.cardBorderTop}`,display:(tab===10||tab===11||tab===12)?"none":"grid",gridTemplateColumns:`repeat(${NAV.length},1fr)`,paddingTop:'10px',paddingBottom:'calc(12px + env(safe-area-inset-bottom))',position:"sticky",bottom:0,zIndex:10,backdropFilter:"blur(16px)"}}>
          {NAV.map(n => {
            const active = tab===n.tab;
            return (
              <button key={n.tab} onClick={()=>{playSound('nav');setTab(n.tab);}} style={{background:"transparent",border:"none",color:active?_T.accent:C.silver,fontSize:11,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,padding:"4px 4px",fontFamily:"Inter,system-ui,sans-serif",fontWeight:active?600:400}}>
                <span style={{fontSize:"28px",lineHeight:1,display:"block"}}>{n.icon}</span>
                <span style={{whiteSpace:"nowrap",fontSize:"11px"}}>{n.label}</span>
                {active && <div style={{width:20,height:2,borderRadius:1,background:`linear-gradient(90deg,${_T.grad1},${_T.grad2})`,marginTop:1}}/>}
              </button>
            );
          })}
        </div>
      </div>
        </div>{/* /sl-main */}

      {/* ── Business public page overlay — sits above everything, unaffected by app-scroll transforms ── */}
      {viewBizPageId && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 9998, background: _T.bg, overflowY: 'auto' }}>
          <BusinessPublicPage businessId={viewBizPageId} lang={lang} onBack={() => setViewBizPageId(null)} />
        </div>
      )}

      {/* Feature tour overlay */}
      {showTour && <FeatureTourModal lang={lang} onClose={() => setShowTour(false)} />}

      {/* ═══════════════════════════════════════════════════════════════════════
          SPORT ONBOARD FLOW — in-app guided experience overlays
      ═══════════════════════════════════════════════════════════════════════ */}
      {/* ── Sport flow: onboarding video intro ── */}
      {sportFlowPhase === 'video_intro' && (
        <VideoIntro onDone={() => setSportFlowPhase('game_prompt')} />
      )}

      {/* ── Sport flow: game running full-screen overlay ── */}
      {sportFlowPhase === 'game_running' && (
        <div style={{position:'fixed',inset:0,zIndex:9400,background:'#0a1220',overflowY:'auto'}}>
          <style>{`@keyframes sf-slide-up{from{opacity:0;transform:translateY(60px)}to{opacity:1;transform:translateY(0)}}`}</style>
          <FocusTapGame
            lang={lang}
            allUsers={[]}
            friends={[]}
            currentUserId={currentUserId}
            userName={userName}
            onBack={() => { setSportFlowPhase('exercise_prompt'); setTab(2); }}
          />
        </div>
      )}

      {/* ── Sport flow: exercise running full-screen overlay ── */}
      {sportFlowPhase === 'exercise_running' && (
        <div style={{position:'fixed',inset:0,zIndex:9400,background:'#0a1220',overflowY:'auto',padding:20}}>
          <SnelleResetSession
            lang={lang}
            onFinish={() => { setSportFlowPhase('dash_tutorial'); setDashTutStep(0); setTab(0); }}
          />
        </div>
      )}

      {['game_prompt','exercise_prompt','dash_tutorial'].includes(sportFlowPhase) && (() => {
        const nl = lang === 'nl';

        /* Shared modal wrapper styles */
        const backdrop = {
          position:'fixed', inset:0, zIndex:9500,
          background:'rgba(5,12,24,0.82)',
          backdropFilter:'blur(6px)',
          display:'flex', alignItems:'flex-end', justifyContent:'center',
          padding:'0 0 32px',
        };
        const card = {
          width:'100%', maxWidth:420,
          background:'linear-gradient(160deg,#0f1e35,#0a1522)',
          border:'1px solid rgba(200,168,75,0.28)',
          borderRadius:'24px 24px 20px 20px',
          padding:'28px 24px 24px',
          boxShadow:'0 -8px 60px rgba(200,168,75,0.12), 0 0 0 1px rgba(200,168,75,0.08)',
          animation:'sf-slide-up .45s cubic-bezier(.34,1.56,.64,1) both',
        };

        /* ── game_prompt ── */
        if (sportFlowPhase === 'game_prompt') return (
          <>
            <style>{`
              @keyframes sf-bounce-in {
                0%   { opacity:0; transform:scale(.55) translateY(40px); }
                55%  { opacity:1; transform:scale(1.07) translateY(-8px); }
                75%  { transform:scale(.96) translateY(4px); }
                88%  { transform:scale(1.02) translateY(-2px); }
                100% { opacity:1; transform:scale(1) translateY(0); }
              }
              @keyframes sf-pulse-ring { 0%,100%{box-shadow:0 0 0 0 rgba(200,168,75,.5)} 60%{box-shadow:0 0 0 14px rgba(200,168,75,0)} }
              @keyframes sf-btn-hover  { 0%,100%{filter:brightness(1)} 50%{filter:brightness(1.12)} }
            `}</style>
            <div style={{
              position:'fixed', inset:0, zIndex:9500,
              background:'rgba(5,12,24,0.82)',
              backdropFilter:'blur(6px)',
              display:'flex', alignItems:'center', justifyContent:'center',
              padding:'0 20px',
            }}>
              <div style={{
                width:'100%', maxWidth:400,
                background:'linear-gradient(160deg,#0f1e35,#0a1522)',
                border:'1px solid rgba(200,168,75,0.28)',
                borderRadius:24,
                padding:'32px 28px 28px',
                boxShadow:'0 0 80px rgba(200,168,75,0.18), 0 0 0 1px rgba(200,168,75,0.08)',
                animation:'sf-bounce-in .6s cubic-bezier(.34,1.56,.64,1) both',
              }}>
                {/* Icon */}
                <div style={{
                  width:60, height:60, borderRadius:18,
                  background:'rgba(200,168,75,.1)',
                  border:'1.5px solid rgba(200,168,75,.35)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:28, marginBottom:18,
                  animation:'sf-pulse-ring 2s ease-in-out infinite',
                }}>🎯</div>

                <div style={{fontSize:22,fontWeight:800,color:'#fff',marginBottom:6,lineHeight:1.2}}>
                  {nl ? 'Start Focus Training' : 'Start Focus Training'}
                </div>
                <div style={{fontSize:13,color:'rgba(255,255,255,.45)',marginBottom:24,lineHeight:1.65}}>
                  {nl
                    ? 'Het wordt snel. Het zal jouw aandacht testen. Klaar?'
                    : 'It will be quick. It will test your attention. Ready?'}
                </div>

                <button
                  onClick={() => setSportFlowPhase('game_running')}
                  style={{
                    width:'100%', padding:'15px 0',
                    background:'linear-gradient(135deg,#C8A84B,#e2c06d)',
                    border:'none', borderRadius:16, cursor:'pointer',
                    fontSize:16, fontWeight:800, color:'#0a1018',
                    letterSpacing:.2,
                    boxShadow:'0 4px 20px rgba(200,168,75,0.4)',
                    animation:'sf-btn-hover 2.5s ease-in-out infinite',
                    transition:'transform .13s, filter .13s',
                  }}
                  onMouseEnter={e=>e.currentTarget.style.transform='scale(1.03)'}
                  onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}
                >
                  {nl ? '⚡ Start' : '⚡ Start'}
                </button>
              </div>
            </div>
          </>
        );

        /* ── exercise_prompt ── */
        if (sportFlowPhase === 'exercise_prompt') return (
          <>
            <style>{`
              @keyframes sf-slide-up { from{opacity:0;transform:translateY(60px)} to{opacity:1;transform:translateY(0)} }
              @keyframes sf-breathe  { 0%,100%{transform:scale(1)} 50%{transform:scale(1.07)} }
            `}</style>
            <div style={backdrop}>
              <div style={{...card, border:'1px solid rgba(123,111,208,.35)', boxShadow:'0 -8px 60px rgba(123,111,208,.14), 0 0 0 1px rgba(123,111,208,.08)'}}>
                <div style={{
                  width:60, height:60, borderRadius:18,
                  background:'rgba(123,111,208,.12)',
                  border:'1.5px solid rgba(123,111,208,.35)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:28, marginBottom:18,
                  animation:'sf-breathe 3s ease-in-out infinite',
                }}>🌿</div>

                <div style={{fontSize:22,fontWeight:800,color:'#fff',marginBottom:4,lineHeight:1.2}}>
                  {nl ? 'Zo, dat was intens!' : 'So that was intense!'}
                </div>
                <div style={{fontSize:15,fontWeight:600,color:'rgba(255,255,255,.6)',marginBottom:10}}>
                  {nl ? 'Even bijkomen?' : "Let's take a break, shall we?"}
                </div>
                <div style={{fontSize:13,color:'rgba(255,255,255,.38)',marginBottom:26,lineHeight:1.65}}>
                  {nl
                    ? 'Met een sessie maximaliseren we jouw grounding en vinden we rust na de focus training.'
                    : 'With a session we maximize your grounding and find some sense and rest after focus training.'}
                </div>

                <button
                  onClick={() => setSportFlowPhase('exercise_running')}
                  style={{
                    width:'100%', padding:'15px 0',
                    background:'linear-gradient(135deg,#7B6FD0,#9b8de8)',
                    border:'none', borderRadius:16, cursor:'pointer',
                    fontSize:16, fontWeight:800, color:'#fff',
                    letterSpacing:.2,
                    boxShadow:'0 4px 20px rgba(123,111,208,0.4)',
                    transition:'transform .13s',
                  }}
                  onMouseEnter={e=>e.currentTarget.style.transform='scale(1.03)'}
                  onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}
                >
                  {nl ? '🌿 Probeer het' : '🌿 Try it'}
                </button>
              </div>
            </div>
          </>
        );

        /* ── dash_tutorial ── */
        if (sportFlowPhase === 'dash_tutorial') {
          const step = DASH_TUT_STEPS[dashTutStep];
          const isLast = dashTutStep >= DASH_TUT_STEPS.length - 1;
          return (
            <>
              <style>{`
                @keyframes sf-slide-up { from{opacity:0;transform:translateY(60px)} to{opacity:1;transform:translateY(0)} }
                @keyframes sf-cursor   { 0%,100%{opacity:1} 50%{opacity:0} }
              `}</style>
              <div style={backdrop}>
                <div style={{...card, border:'1px solid rgba(74,200,122,.28)', boxShadow:'0 -8px 60px rgba(74,200,122,.1), 0 0 0 1px rgba(74,200,122,.07)'}}>
                  {/* Progress dots */}
                  <div style={{display:'flex',gap:6,marginBottom:18}}>
                    {DASH_TUT_STEPS.map((_,i) => (
                      <div key={i} style={{
                        height:4, borderRadius:2,
                        width: i === dashTutStep ? 24 : 8,
                        background: i <= dashTutStep ? '#4AC87A' : 'rgba(255,255,255,.15)',
                        transition:'width .3s, background .3s',
                      }}/>
                    ))}
                  </div>

                  <div style={{fontSize:18,fontWeight:800,color:'#fff',marginBottom:14,lineHeight:1.25}}>
                    {step?.title}
                  </div>
                  <div style={{
                    fontSize:14, color:'rgba(255,255,255,.6)', lineHeight:1.72,
                    marginBottom:26, minHeight:60,
                  }}>
                    {tutTyped}
                    <span style={{animation:'sf-cursor .7s step-end infinite',color:'#4AC87A'}}>|</span>
                  </div>

                  <button
                    onClick={() => {
                      if (isLast) { setSportFlowPhase(null); setLitNavTabs(new Set()); }
                      else        { setDashTutStep(s => s + 1); }
                    }}
                    style={{
                      width:'100%', padding:'14px 0',
                      background: isLast
                        ? 'linear-gradient(135deg,#4AC87A,#34d97b)'
                        : 'rgba(255,255,255,.08)',
                      border: isLast ? 'none' : '1px solid rgba(255,255,255,.12)',
                      borderRadius:15, cursor:'pointer',
                      fontSize:15, fontWeight:700,
                      color: isLast ? '#0a1a10' : '#fff',
                      letterSpacing:.1,
                      boxShadow: isLast ? '0 4px 20px rgba(74,200,122,0.35)' : 'none',
                      transition:'transform .13s',
                    }}
                    onMouseEnter={e=>e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}
                  >
                    {isLast
                      ? (nl ? '🚀 Aan de slag!' : '🚀 Let\'s go!')
                      : (nl ? 'Volgende →' : 'Next →')}
                  </button>
                </div>
              </div>
            </>
          );
        }
        return null;
      })()}
    </ErrorBoundary>
  );
}
